// src/pages/Attendance.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    Badge,
    Alert,
    Spinner,
    Input,
    Label,
    FormGroup,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    Table, Nav, NavItem, NavLink, TabContent, TabPane,
} from 'reactstrap';
import {
    RefreshCw,
    Edit2,
    UserX,
    Clock,
    Calendar,
    AlertCircle,
    Users,
    Save,
    User,
    TrendingUp,
    Plus,
    X,
    FileText,
} from 'react-feather';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { toast } from 'react-toastify';
import { API_URL, apiClient } from '../../helpers/api_helper';
import { format } from 'date-fns';
import LocationSection from 'Components/locationcollapse';
const formatTo12Hour = (timeStr?: string | null) => {
    if (!timeStr) return 'N/A';

    // If timeStr is already in "HH:mm:ss" format
    const parts = timeStr.split(':');
    if (parts.length < 2) return timeStr;

    let hours = parseInt(parts[0], 10);
    const minutes = parts[1];

    if (isNaN(hours)) return timeStr;
    if (hours < 6) {
        hours = hours + 5;
        const mins = parseInt(minutes, 10) + 30;
        if (mins >= 60) {
            hours = hours + 1;
        }
    }

    // Ensure hours is in 0-23 range
    hours = hours % 24;

    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;

    const strHours = hours < 10 ? '0' + hours : String(hours);
    return `${strHours}:${minutes} ${ampm}`;
};

// Types
interface CompanySettings {
    id: number;
    name: string;
    working_from_time: string;
    working_to_time: string;
    full_day_time: number;
    half_day_time: number;
}

interface Designation {
    id: number;
    title: string;
}

interface Employee {
    id: number;
    name: string;
    employee_code: string;
    designation?: Designation;
    status: number;
    overtime?: boolean;
}

interface AttendanceCell {
    hours: number | null;
    regular_hours: number | null;
    overtime_hours: number | null;
    status: 'full_day' | 'half_day' | 'short_day' | 'absent' | 'holiday' | 'sunday' | 'no_out_time' | null;
    is_holiday: boolean;
    is_half_day: boolean;
    is_sunday: boolean;
    is_overtime: boolean;
    is_manual: boolean;
    check_in_photo: string | null;
    check_out_photo: string | null;
    check_out_notes: string | null;
    check_in_location: { latitude: number | null; longitude: number | null } | null;
    check_out_location: { latitude: number | null; longitude: number | null } | null;
    holiday_name?: string;
    check_in: string | null;
    office_id: number | null;
    check_out: string | null;
    check_in_office_name: string | null;
    check_out_office_name: string | null;
    check_in_office_address: string | null;
    check_out_office_address: string | null;
    is_different_office: boolean | null;
    sessions: Array<{
        in: string;
        out: string;
        duration: number;
        allPunches?: string[];
    }>;
    leave?: {
        leave_request_id: number;
        leave_type_id: number | null;
        leave_type_name: string | null;
        leave_type_short: string | null;
        color_hex: string | null;
        is_paid: boolean;
        half_day_type: number | null;
        number_of_days: number;
        reason: string;
    } | null;
    lop?: {
        id: number;
        notes: string;
        date: string;   // YYYY-MM-DD
    } | null;
}

interface EmployeeAttendance {
    employee: Employee;
    attendance: Record<number, AttendanceCell>;
    summary: {
        total_present: number;
        total_half_days: number;
        total_short_days: number;
        total_absent: number;
        total_holidays: number;
        total_hours: number;
        total_overtime_hours: number;
        total_days: number;
        total_working_days: number;
        attendance_percentage: number;
    };
}

interface AttendanceResponse {
    success: boolean;
    data: {
        month: number;
        year: number;
        total_days: number;
        company_settings: CompanySettings;
        employees: EmployeeAttendance[];
        summary: {
            total_employees: number;
            total_present: number;
            total_half_days: number;
            total_short_days: number;
            total_absent: number;
            total_holidays: number;
            total_hours: number;
            total_overtime_hours: number;
            attendance_percentage: number;
        };
        holidays: Array<{ date: string; name: string; description?: string }>;
    };
}

interface LeaveBalanceItem {
    leave_type_id: number;
    leave_type_name: string;
    leave_type_short?: string;
    color_hex?: string;
    is_paid: boolean;
    period: number;
    period_start: string;
    period_end: string;
    allocated: number;
    carried_in: number;
    used: number;
    available: number;
}

interface LeaveBalanceResponse {
    employee: { id: number; name: string; employee_code: string } | null;
    policy: { id: number; name: string; effective_from: string } | null;
    summary: LeaveBalanceItem[];
}
// Helper function to get status color
const getStatusColor = (status: string): string => {
    switch (status) {
        case 'full_day':
            return 'success';
        case 'half_day':
            return 'warning';
        case 'short_day':
            return 'info';
        case 'absent':
            return 'danger';
        case 'holiday':
            return 'primary';
        case 'no_out_time':
            return 'warning'; // or 'info' depending on your preference
        case 'no_in_time':
            return 'danger';
        default:
            return 'secondary';
    }
};

// Helper function to get status label
const getStatusLabel = (status: string): string => {
    switch (status) {
        case 'full_day':
            return 'Full Day';
        case 'half_day':
            return 'Half Day';
        case 'short_day':
            return 'Short Day';
        case 'absent':
            return 'Absent';
        case 'holiday':
            return 'Holiday';
        case 'no_out_time':
            return 'No Check Out';
        case 'no_in_time':
            return 'No Check In';
        default:
            return 'Unknown';
    }
};

// Generate month options
const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: format(new Date(2000, i, 1), 'MMMM'),
}));

// Generate year options (current year ± 5 years)
const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 11 }, (_, i) => ({
    value: currentYear - 5 + i,
    label: (currentYear - 5 + i).toString(),
}));

const Attendance: React.FC<{ mode?: 'standard' | 'ot' | 'sunday' }> = ({ mode = 'standard' }) => {
    // State
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [attendanceData, setAttendanceData] = useState<AttendanceResponse['data'] | null>(null);
    const [error, setError] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    // ---- Add Modal: Tabs ----
    const [addActiveTab, setAddActiveTab] = useState<'attendance' | 'leave'>('attendance');

    // ---- Leave Tab State ----
    const [leaveBalances, setLeaveBalances] = useState<LeaveBalanceItem[]>([]);
    const [leaveLoading, setLeaveLoading] = useState(false);
    const [leaveError, setLeaveError] = useState('');
    const [leaveSubmitting, setLeaveSubmitting] = useState(false);
    const [leaveFormData, setLeaveFormData] = useState({
        leave_type_id: 0,
        from_date: '',
        to_date: '',
        is_paid: true,           // auto-synced from selected leave type's policy item
        reason: '',
        half_day: false,
    });
    // Leave Details Modal
    const [leaveDetailModal, setLeaveDetailModal] = useState(false);
    const [leaveDetailEmployee, setLeaveDetailEmployee] = useState<Employee | null>(null);
    const [leaveDetailDate, setLeaveDetailDate] = useState<string>('');
    const [leaveDetail, setLeaveDetail] = useState<AttendanceCell['leave'] | null>(null);
    // ── Loss of Pay modal state ──
    const [lopModal, setLopModal] = useState(false);
    const [lopNotes, setLopNotes] = useState('');
    const [lopSubmitting, setLopSubmitting] = useState(false);
    const [lopError, setLopError] = useState<string | null>(null);
    const [lopExistingId, setLopExistingId] = useState<number | null>(null);

    // ── LOP detail modal (view existing) ──
    const [lopDetailModal, setLopDetailModal] = useState(false);
    const [lopDetail, setLopDetail] = useState<{ id: number; notes: string; date: string } | null>(null);
    // ── Half-day modal state ──
    const [halfDayModal, setHalfDayModal] = useState(false);
    const [halfDayNotes, setHalfDayNotes] = useState("");
    const [halfDaySubmitting, setHalfDaySubmitting] = useState(false);
    const [halfDayError, setHalfDayError] = useState<string | null>(null);
    // Filters
    const [designations, setDesignations] = useState<Designation[]>([]);
    const [filters, setFilters] = useState({
        designation_id: undefined as number | undefined,
        status: 1,
    });

    // View/Edit Modal
    const [viewModal, setViewModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [selectedAttendance, setSelectedAttendance] = useState<AttendanceCell | null>(null);
    // ── Leave removal state ──
    const [leaveRemoving, setLeaveRemoving] = useState(false);
    const [leaveRemoveError, setLeaveRemoveError] = useState<string | null>(null);
    // Edit Attendance Modal
    const [editModal, setEditModal] = useState(false);
    const [editData, setEditData] = useState({
        employee_id: 0,
        date: '',
        punch_in: '',
        punch_out: '',
        device_serial: 'MANUAL_ENTRY',
        verify_type: '15',
        notes: '',
        office_id: null as number | null,

    });
    const [saving, setSaving] = useState(false);

    // Mark Absent Modal
    const [absentModal, setAbsentModal] = useState(false);
    const [absentData, setAbsentData] = useState({
        employee_id: 0,
        date: '',
        reason: '',
    });
    const [markingAbsent, setMarkingAbsent] = useState(false);

    // Add Attendance Modal
    const [addModal, setAddModal] = useState(false);
    const [addData, setAddData] = useState({
        employee_id: 0,
        date: '',
        punch_in: '',
        punch_out: '',
        device_serial: 'MANUAL_ENTRY',
        verify_type: '15',
        notes: '',
        office_id: null as number | null,

    });
    const [adding, setAdding] = useState(false);


    // ────────────────────────────────────────────────
    // Loss of Pay — open create/edit modal
    // ────────────────────────────────────────────────
    const handleOpenLop = (existingId?: number, existingNotes?: string) => {
        setLopExistingId(existingId ?? null);
        setLopNotes(existingNotes ?? '');
        setLopError(null);
        setLopModal(true);
    };

    // ────────────────────────────────────────────────
    // Loss of Pay — submit (create or update)
    // ────────────────────────────────────────────────
    const handleSubmitLop = async () => {
        if (!selectedEmployee?.id || !selectedDate) {
            toast.error('Missing employee or date');
            return;
        }
        if (!lopNotes.trim()) {
            setLopError('Notes are required');
            return;
        }

        setLopSubmitting(true);
        setLopError(null);

        try {
            let response;
            if (lopExistingId) {
                response = await apiClient.put(`/loss-of-pays/${lopExistingId}`, {
                    notes: lopNotes.trim(),
                });
            } else {
                response = await apiClient.post('/loss-of-pays', {
                    emp_id: selectedEmployee.id,
                    date: selectedDate,
                    notes: lopNotes.trim(),
                });
            }

            const payload = response?.data ?? response;
            if (payload && payload.success === false) {
                const msg = payload.message || 'Failed to save Loss of Pay';
                setLopError(msg);
                toast.error(msg);
                return;
            }

            toast.success(
                lopExistingId
                    ? 'Loss of Pay updated'
                    : 'Marked as Loss of Pay'
            );

            // close all related modals
            setLopModal(false);
            setLopDetailModal(false);
            setViewModal(false);
            setAddModal(false);
            setLopNotes('');
            setLopExistingId(null);

            fetchAttendance();
        } catch (err: any) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                'Failed to save Loss of Pay';
            setLopError(msg);
            toast.error(msg);
        } finally {
            setLopSubmitting(false);
        }
    };

    // ────────────────────────────────────────────────
    // Remove (cancel) a leave request
    // Calls DELETE /api/leave-requests/:id
    // Backend marks the leave as CANCELLED (soft delete)
    // ────────────────────────────────────────────────
    const handleRemoveLeave = async () => {
        if (!leaveDetail?.leave_request_id) {
            toast.error('Missing leave request ID');
            return;
        }

        const ok = window.confirm(
            'Remove this leave?\n\n' +
            'The leave will be cancelled and the employee will no longer be on leave for this date. ' +
            'Attendance for this day will revert to its normal state.'
        );
        if (!ok) return;

        setLeaveRemoving(true);
        setLeaveRemoveError(null);

        try {
            const response = await apiClient.delete(
                `/leave-requests/${leaveDetail.leave_request_id}`
            );

            const payload = response?.data ?? response;
            if (payload && payload.success === false) {
                const msg = payload.message || 'Failed to remove leave';
                setLeaveRemoveError(msg);
                toast.error(msg);
                return;
            }

            toast.success('Leave removed successfully');

            // Close all related modals
            setLeaveDetailModal(false);
            setLeaveDetail(null);
            setLeaveDetailEmployee(null);
            setViewModal(false);
            setAddModal(false);

            // Refresh the attendance grid
            fetchAttendance();
        } catch (err: any) {
            const msg =
                err?.response?.data?.message ||
                err?.data?.message ||
                err?.message ||
                'Failed to remove leave';
            setLeaveRemoveError(msg);
            toast.error(msg);
        } finally {
            setLeaveRemoving(false);
        }
    };

    // ────────────────────────────────────────────────
    // Loss of Pay — deactivate (soft delete)
    // ────────────────────────────────────────────────
    const handleDeactivateLop = async (id: number) => {
        if (!window.confirm(
            'Remove this Loss of Pay?\n\nSalary will no longer be deducted for this day.'
        )) return;

        try {
            await apiClient.patch(`/loss-of-pays/${id}/status`, { status: false });
            toast.success('Loss of Pay removed');
            setLopDetailModal(false);
            setLopDetail(null);
            fetchAttendance();
        } catch (err: any) {
            toast.error(
                err?.response?.data?.message || 'Failed to remove Loss of Pay'
            );
        }
    };

    const handleOpenHalfDay = () => {
        setHalfDayNotes("");
        setHalfDayError(null);
        setHalfDayModal(true);
    };

    const handleSubmitHalfDay = async () => {
        if (!selectedEmployee?.id || !selectedDate) {
            toast.error('Missing employee or date');
            return;
        }

        if (!halfDayNotes.trim()) {
            setHalfDayError('Notes are required');
            toast.error('Please provide a reason for marking half day');
            return;
        }

        setHalfDaySubmitting(true);
        setHalfDayError(null);

        try {
            const response = await apiClient.post('/attendance/half-day', {
                employee_id: selectedEmployee.id,
                date: selectedDate,
                notes: halfDayNotes.trim(),
                is_half_day: true,
            });

            // apiClient usually unwraps { success, message, data } — handle both shapes
            const payload = response?.data ?? response;

            if (payload && payload.success === false) {
                const msg = payload.message || 'Failed to mark half day';
                setHalfDayError(msg);
                toast.error(msg);
                return;
            }

            toast.success(
                payload?.message || 'Day marked as half day successfully!'
            );

            // Reset + close
            setHalfDayModal(false);
            setHalfDayNotes('');
            setHalfDayError(null);

            // Also close the view modal so the calendar refresh is visible
            setViewModal(false);

            // Refresh the month's data
            fetchAttendance();
        } catch (error: any) {
            console.error('Error marking half day:', error);
            const msg =
                error?.response?.data?.message ||
                error?.message ||
                'Failed to mark half day';
            setHalfDayError(msg);
            toast.error(msg);
        } finally {
            setHalfDaySubmitting(false);
        }
    };
    // Fetch attendance data
    const fetchAttendance = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const params: any = {
                year: selectedYear,
                month: selectedMonth,
            };

            if (filters.designation_id) {
                params.designation_id = filters.designation_id;
            }
            if (filters.status !== undefined) {
                params.status = filters.status;
            }

            const response = await apiClient.get('/attendance/monthly', params);

            if (response.success) {
                console.log('Attendance data fetched:', response.data);
                setAttendanceData(response.data);
            } else {
                setError(response.message || 'Failed to fetch attendance');
                toast.error('Failed to fetch attendance');
            }
        } catch (error: any) {
            console.error('Error fetching attendance:', error);
            setError(error.message || 'Failed to fetch attendance');
            toast.error('Failed to fetch attendance');
        } finally {
            setLoading(false);
        }
    }, [selectedYear, selectedMonth, filters]);

    const fetchLeaveBalances = useCallback(async (employee_id: number) => {
        if (!employee_id) return;
        setLeaveLoading(true);
        setLeaveError('');
        try {
            const res: any = await apiClient.get(
                `/leave-policies/summary/${employee_id}`
            );
            // Handle both shapes: bare object or { data: {...} }
            const payload: LeaveBalanceResponse = res?.summary ? res : res?.data ?? res;
            setLeaveBalances(payload?.summary || []);
        } catch (err: any) {
            console.log('error summary', err)
            setLeaveError(err.message || 'Failed to load leave balance');
            setLeaveBalances([]);
        } finally {
            setLeaveLoading(false);
        }
    }, []);
    // Fetch designations for filters
    const fetchDesignations = useCallback(async () => {
        try {
            const response = await apiClient.get('/designations');
            if (response?.data) {
                setDesignations(response.data);
            }
        } catch (error) {
            console.error('Error fetching designations:', error);
        }
    }, []);
    // const fetchOffices = useCallback(async () => {
    //     try {
    //         const response = await apiClient.get('/office-locations');
    //         if (response?.data) {
    //             setOffices(response.data);
    //         }
    //     } catch (error) {
    //         console.error('Error fetching offices:', error);
    //     }
    // }, []);

    // Initial fetch
    useEffect(() => {
        fetchDesignations();
        fetchAttendance();
        // fetchOffices();

    }, []);
    // When Add modal is open and Leave tab is active, force dates = selected date
    useEffect(() => {
        if (addModal && addActiveTab === 'leave' && addData.date) {
            setLeaveFormData(prev => ({
                ...prev,
                from_date: addData.date,
                to_date: addData.date,
            }));
        }
    }, [addModal, addActiveTab, addData.date]);

    // Handle filter change
    const handleFilterChange = (key: string, value: any) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
        }));
    };

    // Clear filters
    const clearFilters = () => {
        setFilters({
            designation_id: undefined,
            status: 1,
        });
    };

    const handleCellClick = (employee: Employee, date: string, attendance: AttendanceCell | null) => {
        setSelectedEmployee(employee);
        setSelectedDate(date);
        setSelectedAttendance(attendance);
        console.log(`Clicked on cell for employee ${employee.name} on date ${date}:`, attendance);
        console.log('📸 Check-in photo URL:', attendance?.check_in_photo);
        console.log('📸 Full check-in photo URL:', attendance?.check_in_photo ? getFullImageUrl(attendance.check_in_photo) : null);
        const isMissingOut = attendance && attendance.check_in && (!attendance.check_out || attendance.check_in === attendance.check_out);

        setEditData({
            employee_id: employee.id,
            date: date,
            punch_in: attendance && attendance.check_in ? attendance.check_in.substring(0, 5) : '',
            punch_out: attendance && attendance.check_out && !isMissingOut ? attendance.check_out.substring(0, 5) : '',
            device_serial: 'MANUAL_ENTRY',
            verify_type: '15',
            office_id: attendance?.office_id || null,
            notes: '', // Reset notes when opening the edit modal
        });
        // if (attendance?.lop) {
        //     setLopDetail(attendance.lop);
        //     setLopDetailModal(true);
        //     return;
        // }
        if (attendance?.leave) {
            setLeaveDetailEmployee(employee);
            setLeaveDetailDate(date);
            setLeaveDetail(attendance.leave);
            setLeaveDetailModal(true);
            return;
        }
        // If absent or no data, open Add modal
        if ((!attendance || attendance.status === 'absent' || attendance.hours === null) && !isMissingOut) {
            setAddData({
                employee_id: employee.id,
                date: date,
                punch_in: '',
                punch_out: '',
                device_serial: 'MANUAL_ENTRY',
                verify_type: '15',
                notes: '',
                office_id: attendance?.office_id || null,
            });
            resetLeaveForm();
            setAddModal(true);

            // ✅ Fetch leave balances for this employee
            fetchLeaveBalances(employee.id);
        } else {
            setViewModal(true);
        }
    };

    // Handle edit
    const handleEdit = () => {
        setViewModal(false);
        setEditModal(true);
    };

    // Handle mark absent
    const handleMarkAbsent = () => {
        setViewModal(false);
        setAbsentData({
            employee_id: selectedEmployee?.id || 0,
            date: selectedDate,
            reason: '',
        });
        setAbsentModal(true);
    };

    // Handle save edit
    const handleSaveEdit = async () => {
        if (!editData.punch_in || !editData.punch_out) {
            toast.error('Please enter both IN and OUT times');
            return;
        }

        setSaving(true);
        try {
            await apiClient.post('/attendance', {
                employee_id: editData.employee_id,
                date: editData.date,
                punch_in: editData.punch_in,
                punch_out: editData.punch_out,
                notes: editData.notes,
                device_serial: editData.device_serial,
                verify_type: editData.verify_type,
                office_id: editData.office_id,
            });

            toast.success('Attendance updated successfully!');
            setEditModal(false);
            fetchAttendance();
        } catch (error: any) {
            console.error('Error saving attendance:', error);
            toast.error(error.message || 'Failed to save attendance');
        } finally {
            setSaving(false);
        }
    };

    // Handle add attendance
    const handleAddAttendance = async () => {
        if (!addData.punch_in || !addData.punch_out) {
            toast.error('Please enter both IN and OUT times');
            return;
        }

        setAdding(true);
        try {
            await apiClient.post('/attendance', {
                employee_id: addData.employee_id,
                date: addData.date,
                punch_in: addData.punch_in,
                punch_out: addData.punch_out,
                device_serial: addData.device_serial,
                verify_type: addData.verify_type,
                notes: addData.notes,
            });

            toast.success('Attendance added successfully!');
            setAddModal(false);
            fetchAttendance();
        } catch (error: any) {
            console.error('Error adding attendance:', error);
            toast.error(error.message || 'Failed to add attendance');
        } finally {
            setAdding(false);
        }
    };

    // Reset leave form + refetch balances whenever Add modal opens
    const resetLeaveForm = () => {
        setLeaveFormData({
            leave_type_id: 0,
            from_date: '',
            to_date: '',
            is_paid: true,
            reason: '',
            half_day: false,
        });
        setLeaveError('');
        setAddActiveTab('attendance');
    };

    // When user picks a leave type → auto-fill is_paid from balance row
    const handleLeaveTypeChange = (leave_type_id: number) => {
        const item = leaveBalances.find(b => b.leave_type_id === leave_type_id);
        setLeaveFormData(prev => ({
            ...prev,
            leave_type_id,
            is_paid: item ? item.is_paid : true,
        }));
    };

    // Submit leave (calls your existing leave request API)
    const handleSubmitLeave = async () => {
        if (!leaveFormData.leave_type_id) {
            setLeaveError('Select a leave type');
            return;
        }
        if (!leaveFormData.from_date || !leaveFormData.to_date) {
            setLeaveError('Select from and to dates');
            return;
        }
        if (new Date(leaveFormData.to_date) < new Date(leaveFormData.from_date)) {
            setLeaveError('End date cannot be before start date');
            return;
        }
        if (!leaveFormData.reason.trim()) {
            setLeaveError('Reason is required');
            return;
        }

        setLeaveSubmitting(true);
        setLeaveError('');
        try {
            await apiClient.post('/leave-requests', {
                employee_id: addData.employee_id,
                leave_type_id: leaveFormData.leave_type_id,
                start_date: leaveFormData.from_date,
                end_date: leaveFormData.to_date,
                is_paid: leaveFormData.is_paid,
                reason: leaveFormData.reason,
                half_day_type: leaveFormData.half_day ? 1 : null,
                status: 1, // auto-approve (adjust if you want pending)
            });
            toast.success('Leave applied successfully');
            setAddModal(false);
            resetLeaveForm();
            fetchAttendance();
        } catch (err: any) {
            setLeaveError(err.message || 'Failed to apply leave');
            toast.error('Failed to apply leave');
        } finally {
            setLeaveSubmitting(false);
        }
    };

    // Handle mark absent confirm
    const handleMarkAbsentConfirm = async () => {
        setMarkingAbsent(true);
        try {
            await apiClient.post('/attendance/absent', {
                employee_id: absentData.employee_id,
                date: absentData.date,
                reason: absentData.reason || undefined,
            });

            toast.success('Employee marked as absent');
            setAbsentModal(false);
            fetchAttendance();
        } catch (error: any) {
            console.error('Error marking absent:', error);
            toast.error(error.message || 'Failed to mark absent');
        } finally {
            setMarkingAbsent(false);
        }
    };

    const getCellBadge = (cell: any, mode?: string) => {
        if (!cell) return <span className="text-muted">-</span>;

        // ✅ Render the normal badge first, then overlay LOP on top if present
        const normalBadge = renderNormalBadge(cell, mode);

        if (cell.lop) {
            return (
                <span style={{ position: 'relative', display: 'inline-block' }}>
                    {normalBadge}
                    {/* LOP marker — small ribbon top-left */}
                    <span
                        style={{
                            position: 'absolute',
                            top: '-6px',
                            left: '-6px',
                            backgroundColor: '#8b0000',
                            color: '#fff',
                            fontSize: '0.5rem',
                            fontWeight: '800',
                            padding: '1px 4px',
                            borderRadius: '3px',
                            border: '1.5px solid #ffd700',
                            letterSpacing: '0.04em',
                            lineHeight: 1.1,
                            boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                            zIndex: 2,
                        }}
                        title="Loss of Pay — click for details"
                    >
                        LOP
                    </span>
                </span>
            );
        }

        return normalBadge;
    };
    const renderNormalBadge = (cell: any, mode?: string) => {
        if (!cell) return <span className="text-muted">-</span>;

        // ✅ HOLIDAY - Check if it has attendance data first
        if (cell.is_holiday) {
            // If holiday has hours (employee worked on holiday), show attendance
            if (cell.hours && cell.hours > 0) {
                const isManual = cell.is_manual || false;
                const isOT = cell.is_overtime || false;

                // For OT mode, show ONLY OT hours on holiday
                if (mode === 'ot') {
                    const otHours = cell.overtime_hours || 0;
                    if (otHours > 0) {
                        return (
                            <span className="badge" style={{
                                backgroundColor: '#1e7e34',
                                color: '#fff',
                                fontSize: '0.7rem',
                                padding: '6px 10px',
                                fontWeight: '600',
                                minWidth: '35px',
                                display: 'inline-block',
                                borderRadius: '4px',
                                position: 'relative'
                            }}>
                                {otHours.toFixed(1)}
                                <span style={{
                                    position: 'absolute',
                                    top: '1px',
                                    right: '3px',
                                    fontSize: '0.4rem',
                                    fontWeight: '700',
                                    lineHeight: 1,
                                    letterSpacing: '0.02em'
                                }}>OT</span>
                            </span>
                        );
                    }
                    return <span className="text-muted">-</span>;
                }

                // Manual + OT on Holiday - Split gradient
                if (isManual && isOT) {
                    return (
                        <span className="badge" style={{
                            background: 'linear-gradient(135deg, #fd7e14 50%, #1e7e34 50%)',
                            color: '#fff',
                            fontSize: '0.7rem',
                            padding: '6px 14px 6px 8px',
                            fontWeight: '700',
                            minWidth: '35px',
                            display: 'inline-block',
                            borderRadius: '4px',
                            position: 'relative'
                        }}>
                            {cell.hours.toFixed(1)}
                            <span style={{
                                position: 'absolute',
                                top: '1px',
                                right: '3px',
                                fontSize: '0.4rem',
                                fontWeight: '700',
                                lineHeight: 1,
                                letterSpacing: '0.02em'
                            }}>OT</span>
                            <span style={{
                                position: 'absolute',
                                top: '1px',
                                left: '3px',
                                fontSize: '0.4rem',
                                fontWeight: '700',
                                lineHeight: 1,
                                letterSpacing: '0.02em'
                            }}>MT</span>
                        </span>
                    );
                }

                // Manual only on Holiday
                if (isManual) {
                    return (
                        <span className="badge" style={{
                            background: 'linear-gradient(135deg, #fd7e14, #ff6b6b)',
                            color: '#fff',
                            fontSize: '0.75rem',
                            padding: '6px 10px',
                            fontWeight: '700',
                            minWidth: '35px',
                            display: 'inline-block',
                            borderRadius: '4px',
                            position: 'relative'
                        }}>
                            {cell.hours.toFixed(1)}
                            <span style={{
                                position: 'absolute',
                                top: '1px',
                                right: '3px',
                                fontSize: '0.4rem',
                                fontWeight: '700',
                                lineHeight: 1,
                                letterSpacing: '0.02em'
                            }}>MT</span>
                        </span>
                    );
                }

                // OT only on Holiday
                if (isOT) {
                    return (
                        <span className="badge" style={{
                            backgroundColor: '#1e7e34',
                            color: '#fff',
                            fontSize: '0.7rem',
                            padding: '6px 14px 6px 8px',
                            fontWeight: '600',
                            minWidth: '35px',
                            display: 'inline-block',
                            borderRadius: '4px',
                            position: 'relative'
                        }}>
                            {cell.hours.toFixed(1)}
                            <span style={{
                                position: 'absolute',
                                top: '1px',
                                right: '3px',
                                fontSize: '0.4rem',
                                fontWeight: '700',
                                lineHeight: 1,
                                letterSpacing: '0.02em'
                            }}>OT</span>
                        </span>
                    );
                }

                // Regular hours on holiday - Show with holiday badge style but with hours
                return (
                    <span className="badge" style={{
                        background: 'linear-gradient(135deg, #007bff, #28a745)',
                        color: '#fff',
                        fontSize: '0.75rem',
                        padding: '6px 10px',
                        fontWeight: '600',
                        minWidth: '35px',
                        display: 'inline-block',
                        borderRadius: '4px'
                    }}>
                        {cell.hours.toFixed(1)}
                    </span>
                );
            }

            // No hours - just show Holiday badge
            return (
                <span className="badge" style={{
                    backgroundColor: '#007bff',
                    color: '#fff',
                    fontSize: '0.75rem',
                    padding: '6px 10px',
                    fontWeight: '600',
                    minWidth: '35px',
                    display: 'inline-block',
                    borderRadius: '4px'
                }}>
                    H
                </span>
            );
        }
        if (cell.leave) {
            const lt = cell.leave;
            return (
                <span
                    className="badge"
                    style={{
                        backgroundColor: lt.color_hex || '#6c757d',
                        color: '#fff',
                        fontSize: '0.7rem',
                        padding: '4px 6px',
                        fontWeight: '600',
                        minWidth: '35px',
                        display: 'inline-block',
                        borderRadius: '4px',
                    }}
                    title={`${lt.leave_type_name}${lt.is_paid ? '' : ' (Unpaid)'}`}
                >
                    {lt.leave_type_short ||
                        lt.leave_type_name?.slice(0, 2).toUpperCase() ||
                        'L'}
                </span>
            );
        }
        // ✅ SUNDAY - Only show Sunday if it's actually sunday AND status is absent (no punches)
        // If there's any attendance (full_day, half_day, no_out_time), show that instead
        if (cell.is_sunday) {
            // ✅ If there are hours (full_day, half_day, etc.) - show attendance
            if (cell.hours && cell.hours > 0) {
                const isManual = cell.is_manual || false;
                const isOT = cell.is_overtime || false;

                // For OT mode, show ONLY OT hours on Sunday
                if (mode === 'ot') {
                    const otHours = cell.overtime_hours || 0;
                    if (otHours > 0) {
                        return (
                            <span className="badge" style={{
                                backgroundColor: '#1e7e34',
                                color: '#fff',
                                fontSize: '0.7rem',
                                padding: '6px 10px',
                                fontWeight: '600',
                                minWidth: '35px',
                                display: 'inline-block',
                                borderRadius: '4px',
                                position: 'relative'
                            }}>
                                {otHours.toFixed(1)}
                                <span style={{
                                    position: 'absolute',
                                    top: '1px',
                                    right: '3px',
                                    fontSize: '0.4rem',
                                    fontWeight: '700',
                                    lineHeight: 1,
                                    letterSpacing: '0.02em'
                                }}>OT</span>
                            </span>
                        );
                    }
                    return <span className="text-muted">-</span>;
                }

                // Manual + OT on Sunday - Split gradient (for non-OT mode)
                if (isManual && isOT) {
                    return (
                        <span className="badge" style={{
                            background: 'linear-gradient(135deg, #fd7e14 50%, #1e7e34 50%)',
                            color: '#fff',
                            fontSize: '0.7rem',
                            padding: '6px 14px 6px 8px',
                            fontWeight: '700',
                            minWidth: '35px',
                            display: 'inline-block',
                            borderRadius: '4px',
                            position: 'relative'
                        }}>
                            {cell.hours.toFixed(1)}
                            <span style={{
                                position: 'absolute',
                                top: '1px',
                                right: '3px',
                                fontSize: '0.4rem',
                                fontWeight: '700',
                                lineHeight: 1,
                                letterSpacing: '0.02em'
                            }}>OT</span>
                            <span style={{
                                position: 'absolute',
                                top: '1px',
                                left: '3px',
                                fontSize: '0.4rem',
                                fontWeight: '700',
                                lineHeight: 1,
                                letterSpacing: '0.02em'
                            }}>MT</span>
                        </span>
                    );
                }

                // Manual only on Sunday
                if (isManual) {
                    return (
                        <span className="badge" style={{
                            background: 'linear-gradient(135deg, #fd7e14, #ff6b6b)',
                            color: '#fff',
                            fontSize: '0.75rem',
                            padding: '6px 10px',
                            fontWeight: '700',
                            minWidth: '35px',
                            display: 'inline-block',
                            borderRadius: '4px',
                            position: 'relative'
                        }}>
                            {cell.hours.toFixed(1)}
                            <span style={{
                                position: 'absolute',
                                top: '1px',
                                right: '3px',
                                fontSize: '0.4rem',
                                fontWeight: '700',
                                lineHeight: 1,
                                letterSpacing: '0.02em'
                            }}>MT</span>
                        </span>
                    );
                }

                // OT only on Sunday
                if (isOT) {
                    return (
                        <span className="badge" style={{
                            backgroundColor: '#1e7e34',
                            color: '#fff',
                            fontSize: '0.7rem',
                            padding: '6px 14px 6px 8px',
                            fontWeight: '600',
                            minWidth: '35px',
                            display: 'inline-block',
                            borderRadius: '4px',
                            position: 'relative'
                        }}>
                            {cell.hours.toFixed(1)}
                            <span style={{
                                position: 'absolute',
                                top: '1px',
                                right: '3px',
                                fontSize: '0.4rem',
                                fontWeight: '700',
                                lineHeight: 1,
                                letterSpacing: '0.02em'
                            }}>OT</span>
                        </span>
                    );
                }

                // Regular Sunday worked
                return (
                    <span className="badge" style={{
                        background: 'linear-gradient(135deg, #6f42c1, #9b59b6)',
                        color: '#fff',
                        fontSize: '0.75rem',
                        padding: '6px 10px',
                        fontWeight: '600',
                        minWidth: '35px',
                        display: 'inline-block',
                        borderRadius: '4px'
                    }}>
                        {cell.hours.toFixed(1)}
                    </span>
                );
            }

            // ✅ If no hours but status is no_out_time (checked in but not out) - show no_out_time
            if (cell.status === 'no_out_time' && cell.check_in) {
                // In OT mode, don't show no_out_time
                if (mode === 'ot') {
                    return <span className="text-muted">-</span>;
                }
                return (
                    <span className="badge" style={{
                        backgroundColor: '#ffffff',
                        color: '#856404',
                        border: '2px solid #ffc107',
                        fontSize: '0.7rem',
                        padding: '4px 8px',
                        fontWeight: '700',
                        minWidth: '35px',
                        display: 'inline-block',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}>
                        ⚠️ IN
                    </span>
                );
            }

            // ✅ Only show Sunday badge if status is absent (no punches at all)
            if (cell.status === 'absent' || !cell.check_in) {
                return (
                    <span className="badge" style={{
                        backgroundColor: '#6f42c1',
                        color: '#fff',
                        fontSize: '0.75rem',
                        padding: '6px 10px',
                        fontWeight: '600',
                        minWidth: '35px',
                        display: 'inline-block',
                        borderRadius: '4px'
                    }}>
                        Sun
                    </span>
                );
            }

            // Fallback for any other case on Sunday
            return <span className="text-muted">-</span>;
        }

        // No Out Time - Single punch only (Yellow badge with warning)
        // Check this before checking for absent, full_day, etc.
        if (cell.status === 'no_out_time' && cell.check_in && !cell.check_out) {
            // In OT mode, don't show no_out_time
            if (mode === 'ot') {
                return <span className="text-muted">-</span>;
            }
            return (
                <span className="badge" style={{
                    backgroundColor: '#ffffff',
                    color: '#856404',
                    border: '2px solid #ffc107',
                    fontSize: '0.7rem',
                    padding: '4px 8px',
                    fontWeight: '700',
                    minWidth: '35px',
                    display: 'inline-block',
                    borderRadius: '4px',
                    cursor: 'pointer'
                }}>
                    ⚠️ IN
                </span>
            );
        }

        // For OT mode: Show ONLY overtime hours
        if (mode === 'ot') {
            const otHours = cell.overtime_hours || 0;
            if (otHours > 0) {
                return (
                    <span className="badge" style={{
                        backgroundColor: '#1e7e34',
                        color: '#fff',
                        fontSize: '0.7rem',
                        padding: '6px 10px',
                        fontWeight: '600',
                        minWidth: '35px',
                        display: 'inline-block',
                        borderRadius: '4px',
                        position: 'relative'
                    }}>
                        {otHours.toFixed(1)}
                        <span style={{
                            position: 'absolute',
                            top: '1px',
                            right: '3px',
                            fontSize: '0.4rem',
                            fontWeight: '700',
                            lineHeight: 1,
                            letterSpacing: '0.02em'
                        }}>OT</span>
                    </span>
                );
            }
            return <span className="text-muted">-</span>;
        }

        // Manual Attendance + Overtime (Both together) - Split gradient (for non-OT mode)
        const isManual = cell.is_manual || false;
        const isOT = cell.is_overtime || false;
        const hasHours = cell.hours && cell.hours > 0;

        if (isManual && isOT && hasHours) {
            return (
                <span className="badge" style={{
                    background: 'linear-gradient(135deg, #fd7e14 50%, #1e7e34 50%)',
                    color: '#fff',
                    fontSize: '0.7rem',
                    padding: '6px 14px 6px 8px',
                    fontWeight: '700',
                    minWidth: '35px',
                    display: 'inline-block',
                    borderRadius: '4px',
                    position: 'relative'
                }}>
                    {cell.hours.toFixed(1)}
                    <span style={{
                        position: 'absolute',
                        top: '1px',
                        right: '3px',
                        fontSize: '0.4rem',
                        fontWeight: '700',
                        lineHeight: 1,
                        letterSpacing: '0.02em'
                    }}>OT</span>
                    <span style={{
                        position: 'absolute',
                        top: '1px',
                        left: '3px',
                        fontSize: '0.4rem',
                        fontWeight: '700',
                        lineHeight: 1,
                        letterSpacing: '0.02em'
                    }}>MT</span>
                </span>
            );
        }

        // Manual Attendance Only - MT on top right
        if (isManual && hasHours) {
            return (
                <span className="badge" style={{
                    background: 'linear-gradient(135deg, #fd7e14, #ff6b6b)',
                    color: '#fff',
                    fontSize: '0.75rem',
                    padding: '6px 10px',
                    fontWeight: '700',
                    minWidth: '35px',
                    display: 'inline-block',
                    borderRadius: '4px',
                    position: 'relative'
                }}>
                    {cell.hours.toFixed(1)}
                    <span style={{
                        position: 'absolute',
                        top: '1px',
                        right: '3px',
                        fontSize: '0.4rem',
                        fontWeight: '700',
                        lineHeight: 1,
                        letterSpacing: '0.02em'
                    }}>MT</span>
                </span>
            );
        }

        // Manual without hours - MT only
        if (isManual) {
            return (
                <span className="badge" style={{
                    background: 'linear-gradient(135deg, #fd7e14, #ff6b6b)',
                    color: '#fff',
                    fontSize: '0.75rem',
                    padding: '6px 10px',
                    fontWeight: '700',
                    minWidth: '35px',
                    display: 'inline-block',
                    borderRadius: '4px',
                    position: 'relative'
                }}>
                    MT
                </span>
            );
        }

        // Absent
        if (cell.status === 'absent') {
            return (
                <span className="badge" style={{
                    backgroundColor: '#dc3545',
                    color: '#fff',
                    fontSize: '0.75rem',
                    padding: '6px 10px',
                    fontWeight: '600',
                    minWidth: '35px',
                    display: 'inline-block',
                    borderRadius: '4px'
                }}>
                    A
                </span>
            );
        }

        // OT Only - Dark Green with OT label on top right
        if (isOT && hasHours) {
            return (
                <span className="badge" style={{
                    backgroundColor: '#1e7e34',
                    color: '#fff',
                    fontSize: '0.7rem',
                    padding: '6px 14px 6px 8px',
                    fontWeight: '600',
                    minWidth: '35px',
                    display: 'inline-block',
                    borderRadius: '4px',
                    position: 'relative'
                }}>
                    {cell.hours.toFixed(1)}
                    <span style={{
                        position: 'absolute',
                        top: '1px',
                        right: '3px',
                        fontSize: '0.4rem',
                        fontWeight: '700',
                        lineHeight: 1,
                        letterSpacing: '0.02em'
                    }}>OT</span>
                </span>
            );
        }

        // Full Day - Green
        if (cell.status === 'full_day') {
            return (
                <span className="badge" style={{
                    backgroundColor: '#28a745',
                    color: '#fff',
                    fontSize: '0.75rem',
                    padding: '6px 10px',
                    fontWeight: '600',
                    minWidth: '35px',
                    display: 'inline-block',
                    borderRadius: '4px'
                }}>
                    {cell.hours?.toFixed(1)}
                </span>
            );
        }

        // Half Day - Blue
        if (cell.status === 'half_day') {
            return (
                <span className="badge" style={{
                    backgroundColor: '#17a2b8',
                    color: '#fff',
                    fontSize: '0.75rem',
                    padding: '6px 10px',
                    fontWeight: '600',
                    minWidth: '35px',
                    display: 'inline-block',
                    borderRadius: '4px'
                }}>
                    {cell.hours?.toFixed(1)}
                </span>
            );
        }

        // Short Day - Orange
        if (cell.status === 'short_day') {
            return (
                <span className="badge" style={{
                    backgroundColor: '#fd7e14',
                    color: '#fff',
                    fontSize: '0.75rem',
                    padding: '6px 10px',
                    fontWeight: '600',
                    minWidth: '35px',
                    display: 'inline-block',
                    borderRadius: '4px'
                }}>
                    {cell.hours?.toFixed(1)}
                </span>
            );
        }

        // Default - show hours if available
        if (hasHours) {
            return (
                <span className="badge" style={{
                    backgroundColor: '#6c757d',
                    color: '#fff',
                    fontSize: '0.75rem',
                    padding: '6px 10px',
                    fontWeight: '600',
                    minWidth: '35px',
                    display: 'inline-block',
                    borderRadius: '4px'
                }}>
                    {cell.hours.toFixed(1)}
                </span>
            );
        }

        return <span className="text-muted">-</span>;
    };

    const getFullImageUrl = (url: any) => {
        console.log('📸 Original URL:', url);
        if (!url) return null;

        const IMAGE_URL = process.env.REACT_APP_IMAGE_URL;

        // If it starts with http:// or https://, extract filename and use /uploads/attendance/
        if (url.startsWith('http://') || url.startsWith('https://')) {
            // Extract filename (everything after the last /)
            const filename = url.substring(url.lastIndexOf('/') + 1);
            // Return with proper path
            return `${IMAGE_URL}/uploads/attendance/${filename}`;
        }

        // If it already has uploads/attendance/, just prepend IMAGE_URL
        if (url.startsWith('uploads/attendance/')) {
            return `${IMAGE_URL}/${url}`;
        }

        // If it's just a filename
        return `${IMAGE_URL}/uploads/attendance/${url}`;
    };

    const getSafeImageUrl = (url: any) => {
        const fullUrl = getFullImageUrl(url);
        return fullUrl || ''; // Return empty string if null
    };
    const formatDate = (dateString: string): string => {
        const date = new Date(dateString + 'T00:00:00');
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };
    // Get cell tooltip - Updated with better information
    const getCellTooltip = (cell: AttendanceCell): string => {
        if (!cell) return 'No data';
        if (cell.lop) {
            return `🚫 LOSS OF PAY\n${cell.lop.notes}\n\nSalary will be deducted for this day.`;
        }

        if (cell.is_holiday) {
            return `Holiday: ${cell.holiday_name || 'Holiday'}`;
        }

        if (cell.is_sunday) {
            if (cell.hours && cell.hours > 0) {
                return `Sunday Worked: ${cell.hours?.toFixed(1)} hrs`;
            }
            return 'Sunday (Weekly Off)';
        }

        if (cell.status === 'no_out_time') {
            return `⚠️ Check-in only: ${formatTo12Hour(cell.check_in)}\nClick to add check-out time`;
        }

        if (cell.status === 'absent') {
            return 'Absent';
        }

        if (cell.is_manual) {
            return `Manual Entry: ${cell.hours?.toFixed(1)} hrs`;
        }

        let tooltip = '';
        if (cell.hours && cell.hours > 0) {
            tooltip += `${cell.hours?.toFixed(1)} hrs`;
        }

        if (cell.regular_hours && cell.regular_hours > 0) {
            tooltip += `\nRegular: ${cell.regular_hours.toFixed(1)} hrs`;
        }

        if (cell.overtime_hours && cell.overtime_hours > 0) {
            tooltip += `\nOT: ${cell.overtime_hours.toFixed(1)} hrs`;
        }

        if (cell.check_in) {
            tooltip += `\nIN: ${formatTo12Hour(cell.check_in)}`;
        }
        if (cell.check_out) {
            tooltip += `\nOUT: ${formatTo12Hour(cell.check_out)}`;
        }

        return tooltip || 'No data';
    };

    // Get days in month
    const daysInMonth = attendanceData ? attendanceData.total_days : 0;
    const dayLabels = Array.from({ length: daysInMonth }, (_, i) => i + 1).filter(day => {
        if (mode !== 'sunday') return true;
        const date = new Date(selectedYear, selectedMonth - 1, day);
        return date.getDay() === 0;
    });

    const handleExportExcel = async () => {
        try {
            setExporting(true);

            const params = new URLSearchParams({
                year: selectedYear.toString(),
                month: selectedMonth.toString()
            });

            if (filters.designation_id) {
                params.append('designation_id', filters.designation_id.toString());
            }
            if (filters.status !== undefined && filters.status !== null) {
                params.append('status', filters.status.toString());
            }

            // ✅ Use fetch directly to bypass axios interceptors
            const token = localStorage.getItem('accessToken');
            const url = `${API_URL}/attendance/export?${params.toString()}`;

            console.log('📊 Fetch URL:', url);

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                },
            });

            console.log('📊 Response status:', response.status);
            console.log('📊 Response ok:', response.ok);

            if (!response.ok) {
                const errorText = await response.text();
                console.log('📊 Error response:', errorText);
                try {
                    const errorJson = JSON.parse(errorText);
                    throw new Error(errorJson.message || 'Export failed');
                } catch {
                    throw new Error(`Server error: ${response.status} ${errorText.substring(0, 100)}`);
                }
            }

            // ✅ Get the blob
            const blob = await response.blob();
            console.log('📊 Blob size:', blob.size, 'type:', blob.type);

            if (blob.size === 0) {
                throw new Error('Received empty file from server');
            }

            // Get filename from Content-Disposition header
            const contentDisposition = response.headers.get('content-disposition') ||
                response.headers.get('Content-Disposition');

            console.log('📊 Content-Disposition:', contentDisposition);

            let filename = `Attendance_${selectedMonth}_${selectedYear}.xlsx`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (match && match[1]) {
                    filename = match[1].replace(/['"]/g, '');
                }
            }

            console.log('📊 Downloading:', filename);

            // Create download link
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => {
                URL.revokeObjectURL(link.href);
            }, 100);

            toast.success('Attendance exported successfully!');
        } catch (error: any) {
            console.error('❌ Export error:', error);
            toast.error(error.message || 'Failed to export attendance');
        } finally {
            setExporting(false);
        }
    };

    const handleOtExportExcel = async () => {
        try {
            setExporting(true);

            // Check if there's data to export
            if (!attendanceData || attendanceData.employees.length === 0) {
                toast.warning('No attendance data available to export');
                return;
            }

            // Check if there are any employees with OT
            const employeesWithOT = attendanceData.employees.filter(
                item => item.employee.overtime === true && item.summary.total_overtime_hours > 0
            );

            if (employeesWithOT.length === 0) {
                toast.warning('No employees with overtime for this period');
                return;
            }

            const params = new URLSearchParams({
                year: selectedYear.toString(),
                month: selectedMonth.toString()
            });

            if (filters.designation_id) {
                params.append('designation_id', filters.designation_id.toString());
            }
            if (filters.status !== undefined && filters.status !== null) {
                params.append('status', filters.status.toString());
            }

            const token = localStorage.getItem('accessToken');
            const url = `${API_URL}/attendance/export-ot?${params.toString()}`;

            console.log('📊 Fetching OT Excel from:', url);

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                },
            });

            if (!response.ok) {
                let errorMessage = 'Export failed';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.message || errorMessage;
                } catch {
                    const errorText = await response.text();
                    if (errorText) {
                        errorMessage = errorText.substring(0, 200);
                    }
                }
                throw new Error(errorMessage);
            }

            const blob = await response.blob();

            if (blob.size === 0) {
                throw new Error('Received empty file from server');
            }

            // Get filename from Content-Disposition header
            const contentDisposition = response.headers.get('content-disposition') ||
                response.headers.get('Content-Disposition');

            let filename = `OT_Attendance_${format(new Date(selectedYear, selectedMonth - 1, 1), 'MMMM_yyyy')}.xlsx`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (match && match[1]) {
                    filename = match[1].replace(/['"]/g, '');
                }
            }

            // Create download link
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up
            setTimeout(() => {
                URL.revokeObjectURL(link.href);
            }, 100);

            toast.success(`OT Attendance exported successfully as ${filename}`);

        } catch (error: any) {
            console.error('❌ OT Export error:', error);
            toast.error(error.message || 'Failed to export OT attendance');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb
                    title={mode === 'ot' ? "OT Attendance" : mode === 'sunday' ? "Sunday Attendance" : "Attendance"}
                    pageTitle={mode === 'ot' ? "OT Attendance" : mode === 'sunday' ? "Sunday Attendance" : "Attendance"}
                />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div>
                                    <h5 className="card-title mb-0">
                                        <Calendar size={18} className="me-2" />
                                        {mode === 'ot' ? "OT Attendance" : mode === 'sunday' ? "Sunday Attendance" : "Monthly Attendance"}
                                    </h5>
                                    {attendanceData && (
                                        <small className="text-muted">
                                            {format(new Date(selectedYear, selectedMonth - 1, 1), 'MMMM yyyy')}
                                            {' '}
                                            <Badge color="info" className="ms-2">
                                                {mode === 'ot'
                                                    ? attendanceData.employees.filter(item => item.employee.overtime === true).length
                                                    : attendanceData.summary.total_employees} Employees
                                            </Badge>
                                        </small>
                                    )}
                                </div>
                                <div className="d-flex gap-2">
                                    {/* ✅ Show Export Excel button only for OT mode */}
                                    {mode === 'ot' && (
                                        <Button
                                            color="success"
                                            size="sm"
                                            onClick={handleOtExportExcel}
                                            disabled={exporting || loading || !attendanceData || attendanceData.employees.length === 0}
                                        >
                                            {exporting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                    Exporting...
                                                </>
                                            ) : (
                                                <>
                                                    <FileText size={14} className="me-1" />
                                                    Export Excel
                                                </>
                                            )}
                                        </Button>
                                    )}
                                    <Button
                                        color="secondary"
                                        size="sm"
                                        onClick={() => fetchAttendance()}
                                        disabled={loading}
                                    >
                                        <RefreshCw size={14} className="me-1" />
                                        Refresh
                                    </Button>
                                </div>
                            </CardHeader>

                            <CardBody>
                                {/* Filters - Always Visible */}
                                <div className="mb-3 p-3 border rounded bg-light">
                                    <Row className="g-3 align-items-end">
                                        <Col md={3}>
                                            <FormGroup>
                                                <Label className="form-label-sm">Month</Label>
                                                <Input
                                                    type="select"
                                                    value={selectedMonth}
                                                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                                    bsSize="sm"
                                                >
                                                    {monthOptions.map((m) => (
                                                        <option key={m.value} value={m.value}>
                                                            {m.label}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md={3}>
                                            <FormGroup>
                                                <Label className="form-label-sm">Year</Label>
                                                <Input
                                                    type="select"
                                                    value={selectedYear}
                                                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                                    bsSize="sm"
                                                >
                                                    {yearOptions.map((y) => (
                                                        <option key={y.value} value={y.value}>
                                                            {y.label}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md={3}>
                                            <FormGroup>
                                                <Label className="form-label-sm">Designation</Label>
                                                <Input
                                                    type="select"
                                                    value={filters.designation_id || ''}
                                                    onChange={(e) => handleFilterChange('designation_id', e.target.value ? parseInt(e.target.value) : undefined)}
                                                    bsSize="sm"
                                                >
                                                    <option value="">All Designations</option>
                                                    {designations.map((d) => (
                                                        <option key={d.id} value={d.id}>
                                                            {d.title}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md={3}>
                                            <FormGroup>
                                                <Label className="form-label-sm d-none d-md-block">&nbsp;</Label>
                                                <div className="d-flex gap-2">
                                                    <Button color="light" size="sm" onClick={clearFilters} className="w-50">
                                                        Clear
                                                    </Button>
                                                    <Button color="primary" size="sm" onClick={fetchAttendance} className="w-50">
                                                        Apply
                                                    </Button>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </div>

                                {/* Error Alert */}
                                {error && (
                                    <Alert color="danger" className="mb-3">
                                        <AlertCircle size={16} className="me-2" />
                                        {error}
                                    </Alert>
                                )}

                                {/* Legend - All Inline Styles for Consistency */}
                                {attendanceData && mode === 'standard' && (
                                    <div className='d-flex justify-content-between align-items-center mb-2'>
                                        <div className="mb-2 d-flex flex-wrap gap-2">
                                            {/* Full Day - Green */}
                                            <small className="d-flex align-items-center me-2">
                                                <span
                                                    className="me-1"
                                                    style={{
                                                        backgroundColor: '#28a745',
                                                        color: '#fff',
                                                        fontSize: '0.75rem',
                                                        padding: '2px 8px',
                                                        borderRadius: '4px',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    {attendanceData.company_settings.full_day_time.toFixed(1)}
                                                </span>
                                                Full Day
                                            </small>

                                            {/* Half Day - Blue */}
                                            <small className="d-flex align-items-center me-2">
                                                <span
                                                    className="me-1"
                                                    style={{
                                                        backgroundColor: '#17a2b8',
                                                        color: '#fff',
                                                        fontSize: '0.75rem',
                                                        padding: '2px 8px',
                                                        borderRadius: '4px',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    {attendanceData.company_settings.half_day_time.toFixed(1)}
                                                </span>
                                                Half Day
                                            </small>

                                            {/* Short Day - Orange */}
                                            {/* <small className="d-flex align-items-center me-2">
                                                <span
                                                    className="me-1"
                                                    style={{
                                                        backgroundColor: '#fd7e14',
                                                        color: '#fff',
                                                        fontSize: '0.75rem',
                                                        padding: '2px 8px',
                                                        borderRadius: '4px',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    &lt;{attendanceData.company_settings.half_day_time.toFixed(1)}
                                                </span>
                                                Short Day
                                            </small> */}

                                            {/* No Out Time - White with Yellow Border (Matches table badge) */}
                                            <small className="d-flex align-items-center me-2">
                                                <span
                                                    className="me-1"
                                                    style={{
                                                        backgroundColor: '#ffffff',
                                                        color: '#856404',
                                                        border: '2px solid #ffc107',
                                                        fontSize: '0.75rem',
                                                        padding: '4px 12px',
                                                        borderRadius: '4px',
                                                        fontWeight: '700',
                                                        minWidth: '35px',
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    ⚠️ IN
                                                </span>
                                                No Out Time
                                            </small>

                                            {/* Absent - Red */}
                                            <small className="d-flex align-items-center me-2">
                                                <span
                                                    className="me-1"
                                                    style={{
                                                        backgroundColor: '#dc3545',
                                                        color: '#fff',
                                                        fontSize: '0.75rem',
                                                        padding: '2px 8px',
                                                        borderRadius: '4px',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    A
                                                </span>
                                                Absent
                                            </small>

                                            {/* Holiday - Blue */}
                                            <small className="d-flex align-items-center me-2">
                                                <span
                                                    className="me-1"
                                                    style={{
                                                        backgroundColor: '#007bff',
                                                        color: '#fff',
                                                        fontSize: '0.75rem',
                                                        padding: '2px 8px',
                                                        borderRadius: '4px',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    H
                                                </span>
                                                Holiday
                                            </small>

                                            {/* Sunday - Purple */}
                                            <small className="d-flex align-items-center me-2">
                                                <span
                                                    className="me-1"
                                                    style={{
                                                        backgroundColor: '#6f42c1',
                                                        color: '#fff',
                                                        fontSize: '0.75rem',
                                                        padding: '2px 8px',
                                                        borderRadius: '4px',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    Sun
                                                </span>
                                                Sunday
                                            </small>

                                            {/* Manual Attendance - Gradient */}
                                            <small className="d-flex align-items-center me-2">
                                                <span
                                                    className="me-1"
                                                    style={{
                                                        background: 'linear-gradient(135deg, #fd7e14, #ff6b6b)',
                                                        color: '#fff',
                                                        fontSize: '0.75rem',
                                                        padding: '2px 8px',
                                                        borderRadius: '4px',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    MT
                                                </span>
                                                Manual Entry
                                            </small>

                                            {/* Overtime - Dark Green */}
                                            <small className="d-flex align-items-center">
                                                <span
                                                    className="me-1 text-center"
                                                    style={{
                                                        backgroundColor: '#1e7e34',
                                                        color: '#fff',
                                                        fontSize: '0.75rem',
                                                        padding: '2px 14px 2px 8px',
                                                        borderRadius: '4px',
                                                        fontWeight: '600',
                                                        position: 'relative',
                                                        display: 'inline-block',
                                                        minWidth: '38px'
                                                    }}
                                                >
                                                    {attendanceData.company_settings.full_day_time.toFixed(1)}
                                                    <span style={{
                                                        position: 'absolute',
                                                        top: '1px',
                                                        right: '2px',
                                                        fontSize: '0.45rem',
                                                        fontWeight: '700',
                                                        lineHeight: 1,
                                                        letterSpacing: '0.02em'
                                                    }}>OT</span>
                                                </span>
                                                Overtime
                                            </small>
                                            {/* Loss of Pay - Maroon with gold border */}
                                            <small className="d-flex align-items-center me-2">
                                                <span
                                                    className="me-1"
                                                    style={{
                                                        backgroundColor: '#8b0000',
                                                        color: '#fff',
                                                        fontSize: '0.75rem',
                                                        padding: '2px 10px',
                                                        borderRadius: '4px',
                                                        fontWeight: '800',
                                                        border: '2px solid #ffd700',
                                                        letterSpacing: '0.04em',
                                                    }}
                                                >
                                                    LOP
                                                </span>
                                                Loss of Pay
                                            </small>
                                        </div>
                                        <Button
                                            color="success"
                                            size="sm"
                                            onClick={handleExportExcel}
                                            disabled={loading || exporting || !attendanceData}
                                        >
                                            {exporting ? (
                                                <>
                                                    <Spinner size="sm" className="me-1" />
                                                    Exporting...
                                                </>
                                            ) : (
                                                <>
                                                    <FileText size={14} className="me-1" />
                                                    Export Excel
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                )}

                                {/* Attendance Table */}
                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner color="primary" className="me-2" />
                                        <span>Loading data...</span>
                                    </div>
                                ) : !attendanceData || attendanceData.employees.length === 0 ? (
                                    <div className="text-center py-5">
                                        <Clock size={48} className="text-muted mb-3" />
                                        <h5>No attendance data found</h5>
                                        <p className="text-muted">
                                            {format(new Date(selectedYear, selectedMonth - 1, 1), 'MMMM yyyy')} has no attendance records
                                        </p>
                                    </div>
                                ) : (
                                    <div className="table-responsive" style={{ maxHeight: '70vh', overflow: 'auto' }}>
                                        <Table bordered hover size="sm" className="mb-0 attendance-table" style={{ fontSize: '0.9rem' }}>
                                            <thead className="sticky-top bg-white" style={{ zIndex: 10 }}>
                                                <tr>
                                                    <th className="text-center" style={{
                                                        width: '55px',
                                                        minWidth: '55px',
                                                        maxWidth: '55px',
                                                        position: 'sticky',
                                                        left: 0,
                                                        zIndex: 11,
                                                        backgroundColor: 'white',
                                                        boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
                                                        fontSize: '0.85rem',
                                                        fontWeight: '600'
                                                    }}>
                                                        #
                                                    </th>
                                                    <th className="text-center" style={{
                                                        width: '80px',
                                                        minWidth: '80px',
                                                        maxWidth: '80px',
                                                        position: 'sticky',
                                                        left: 55,
                                                        zIndex: 11,
                                                        backgroundColor: 'white',
                                                        boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
                                                        fontSize: '0.75rem',
                                                        fontWeight: '600',
                                                        color: '#6c757d'
                                                    }}>
                                                        Code
                                                    </th>
                                                    <th style={{
                                                        width: '150px',
                                                        minWidth: '150px',
                                                        maxWidth: '150px',
                                                        position: 'sticky',
                                                        left: 135,
                                                        zIndex: 11,
                                                        backgroundColor: 'white',
                                                        boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
                                                        fontSize: '0.85rem',
                                                        fontWeight: '600'
                                                    }}>
                                                        Employee
                                                    </th>
                                                    {dayLabels.map((day) => {
                                                        // Check if this day is in the future (for current month/year)
                                                        const currentDate = new Date();
                                                        const isCurrentMonth = currentDate.getMonth() + 1 === selectedMonth;
                                                        const isCurrentYear = currentDate.getFullYear() === selectedYear;
                                                        const isFutureDate = isCurrentMonth && isCurrentYear && day > currentDate.getDate();

                                                        return (
                                                            <th key={day} className="text-center" style={{
                                                                width: '60px',
                                                                minWidth: '60px',
                                                                maxWidth: '60px',
                                                                fontSize: '0.8rem',
                                                                fontWeight: '600',
                                                                opacity: isFutureDate ? 0.5 : 1
                                                            }}>
                                                                {day}
                                                            </th>
                                                        );
                                                    })}
                                                    <th style={{
                                                        width: '85px',
                                                        minWidth: '85px',
                                                        maxWidth: '85px',
                                                        fontSize: '0.85rem',
                                                        fontWeight: '600'
                                                    }}>{mode === 'ot' ? 'Total OT' : mode === 'sunday' ? 'Total Sun' : 'Total'}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {attendanceData.employees.filter(item => mode !== 'ot' || item.employee.overtime === true).map((item, index) => {
                                                    const employee = item.employee;

                                                    const days = dayLabels.map((day) => {
                                                        const cell = item.attendance[day] || null;
                                                        return cell;
                                                    });

                                                    return (
                                                        <tr key={employee.id}>
                                                            <td className="text-center" style={{
                                                                width: '55px',
                                                                minWidth: '55px',
                                                                maxWidth: '55px',
                                                                position: 'sticky',
                                                                left: 0,
                                                                zIndex: 5,
                                                                backgroundColor: 'white',
                                                                boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
                                                                fontSize: '0.9rem',
                                                                verticalAlign: 'middle'
                                                            }}>
                                                                {index + 1}
                                                            </td>
                                                            <td style={{
                                                                width: '80px',
                                                                minWidth: '80px',
                                                                maxWidth: '80px',
                                                                position: 'sticky',
                                                                left: 55,
                                                                zIndex: 5,
                                                                backgroundColor: 'white',
                                                                boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
                                                                fontSize: '0.9rem',
                                                                verticalAlign: 'middle'
                                                            }}>
                                                                {employee.employee_code}
                                                            </td>
                                                            <td style={{
                                                                width: '150px',
                                                                minWidth: '150px',
                                                                maxWidth: '150px',
                                                                position: 'sticky',
                                                                left: 135,
                                                                zIndex: 5,
                                                                backgroundColor: 'white',
                                                                boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
                                                                fontSize: '0.9rem',
                                                                verticalAlign: 'middle'
                                                            }}>
                                                                <div className="d-flex flex-column">
                                                                    <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                                                                        {employee.name}
                                                                    </span>
                                                                    {employee.designation?.title && (
                                                                        <small className="text-muted" style={{ fontSize: '10px', marginTop: '1px', lineHeight: '1.2' }}>
                                                                            {employee.designation.title}
                                                                        </small>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            {days.map((cell, cellIndex) => {
                                                                const actualDay = dayLabels[cellIndex];
                                                                const dateKey = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(actualDay).padStart(2, '0')}`;

                                                                // Check if this day is in the future
                                                                const currentDate = new Date();
                                                                const isCurrentMonth = currentDate.getMonth() + 1 === selectedMonth;
                                                                const isCurrentYear = currentDate.getFullYear() === selectedYear;
                                                                const isFutureDate = isCurrentMonth && isCurrentYear && actualDay > currentDate.getDate();

                                                                // If it's a future date, show empty cell
                                                                if (isFutureDate) {
                                                                    return (
                                                                        <td
                                                                            key={cellIndex}
                                                                            className="text-center p-0"
                                                                            style={{
                                                                                width: '60px',
                                                                                minWidth: '60px',
                                                                                maxWidth: '60px',
                                                                                cursor: 'default',
                                                                                verticalAlign: 'middle',
                                                                                opacity: 0.5
                                                                            }}
                                                                        >
                                                                            <div className="p-1 text-muted">-</div>
                                                                        </td>
                                                                    );
                                                                }

                                                                // ✅ OT Mode: Only show cells with overtime
                                                                if (mode === 'ot') {
                                                                    // If no cell or no overtime hours, show empty
                                                                    if (!cell || !cell.is_overtime || cell.overtime_hours === 0 || cell.overtime_hours === null) {
                                                                        return (
                                                                            <td
                                                                                key={cellIndex}
                                                                                className="text-center p-0"
                                                                                style={{
                                                                                    width: '60px',
                                                                                    minWidth: '60px',
                                                                                    maxWidth: '60px',
                                                                                    cursor: 'default',
                                                                                    verticalAlign: 'middle'
                                                                                }}
                                                                            >
                                                                                <div className="p-1 text-muted">-</div>
                                                                            </td>
                                                                        );
                                                                    }
                                                                    // Show OT cell
                                                                    return (
                                                                        <td
                                                                            key={cellIndex}
                                                                            className="text-center p-0"
                                                                            style={{
                                                                                width: '60px',
                                                                                minWidth: '60px',
                                                                                maxWidth: '60px',
                                                                                cursor: 'pointer',
                                                                                verticalAlign: 'middle'
                                                                            }}
                                                                            onClick={() => handleCellClick(employee, dateKey, cell)}
                                                                        >
                                                                            <div className="p-1" title={getCellTooltip(cell)}>
                                                                                {getCellBadge(cell, mode)}
                                                                            </div>
                                                                        </td>
                                                                    );
                                                                }

                                                                // ✅ Standard & Sunday Mode: Show all attendance
                                                                // const isClickable = mode === 'sunday' || (cell && cell.status !== 'sunday');
                                                                const isClickable = true
                                                                return (
                                                                    <td
                                                                        key={cellIndex}
                                                                        className="text-center p-0"
                                                                        style={{
                                                                            width: '60px',
                                                                            minWidth: '60px',
                                                                            maxWidth: '60px',
                                                                            cursor: isClickable ? 'pointer' : 'default',
                                                                            verticalAlign: 'middle'
                                                                        }}
                                                                        onClick={() => isClickable && handleCellClick(employee, dateKey, cell)}
                                                                    >
                                                                        {cell ? (
                                                                            <div
                                                                                className="p-1"
                                                                                title={getCellTooltip(cell)}
                                                                            >
                                                                                {getCellBadge(cell, mode)}
                                                                            </div>
                                                                        ) : (
                                                                            <div className="p-1 text-muted" style={{ fontSize: '0.85rem' }}>-</div>
                                                                        )}
                                                                    </td>
                                                                );
                                                            })}
                                                            {/* ✅ FIXED: Total column */}
                                                            {(() => {
                                                                let totalValue = '';
                                                                let totalColor = '';

                                                                if (mode === 'ot') {
                                                                    // ✅ Show ONLY OT hours, not total hours
                                                                    const otHours = item.summary.total_overtime_hours || 0;
                                                                    totalValue = `${otHours.toFixed(1)}h`;
                                                                    totalColor = otHours > 0 ? 'text-success' : '';
                                                                } else if (mode === 'sunday') {
                                                                    let sundayHours = 0;
                                                                    dayLabels.forEach(day => {
                                                                        const cell = item.attendance[day];
                                                                        if (cell && cell.hours !== null) {
                                                                            sundayHours += cell.hours;
                                                                        }
                                                                    });
                                                                    totalValue = `${sundayHours.toFixed(1)}h`;
                                                                    totalColor = sundayHours > 0 ? 'text-info' : '';
                                                                } else {
                                                                    // ✅ Standard mode - show total hours
                                                                    const totalHours = item.summary.total_hours || 0;
                                                                    totalValue = `${totalHours.toFixed(1)}h`;
                                                                    totalColor = totalHours > 0 ? 'text-primary' : '';
                                                                }

                                                                return (
                                                                    <td className="text-center" style={{
                                                                        width: '85px',
                                                                        minWidth: '85px',
                                                                        maxWidth: '85px',
                                                                        fontSize: '0.9rem',
                                                                        verticalAlign: 'middle'
                                                                    }}>
                                                                        <span className={`fw-semibold ${totalColor}`}>
                                                                            {totalValue}
                                                                        </span>
                                                                    </td>
                                                                );
                                                            })()}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}

                                {/* View Modal - Grid Layout */}
                                <Modal isOpen={viewModal} toggle={() => setViewModal(false)} size="lg">
                                    <ModalHeader toggle={() => setViewModal(false)}>
                                        <div className="d-flex align-items-center">
                                            <User size={18} className="me-2" />
                                            <div>
                                                <div className="fw-semibold">
                                                    {selectedEmployee?.name}
                                                </div>
                                                <small className="text-muted">
                                                    {selectedEmployee?.employee_code} • {selectedEmployee?.designation?.title || ''}
                                                </small>
                                            </div>
                                        </div>
                                    </ModalHeader>
                                    <ModalBody>
                                        <Row className="g-2">
                                            {selectedAttendance?.lop && (
                                                <div
                                                    className="mb-3 p-3 rounded d-flex align-items-start gap-2"
                                                    style={{
                                                        background: 'linear-gradient(135deg, #fff5f5, #ffe5e5)',
                                                        border: '2px solid #8b0000',
                                                    }}
                                                >
                                                    <span style={{ fontSize: '22px', lineHeight: 1 }}>🚫</span>
                                                    <div className="flex-grow-1">
                                                        <div className="d-flex align-items-center gap-2 mb-1">
                                                            <span
                                                                style={{
                                                                    backgroundColor: '#8b0000',
                                                                    color: '#fff',
                                                                    fontSize: '0.65rem',
                                                                    fontWeight: '800',
                                                                    padding: '2px 8px',
                                                                    borderRadius: '4px',
                                                                    border: '1.5px solid #ffd700',
                                                                    letterSpacing: '0.05em',
                                                                }}
                                                            >
                                                                LOSS OF PAY
                                                            </span>
                                                            <small className="text-muted">
                                                                Salary for this day will be deducted
                                                            </small>
                                                        </div>
                                                        <div className="small" style={{ color: '#4a0000', whiteSpace: 'pre-wrap' }}>
                                                            <strong>Reason:</strong> {selectedAttendance.lop.notes}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            <Col md={6}>
                                                <div className="p-2 bg-light rounded">
                                                    <small className="text-muted">Date</small>
                                                    <div className="fw-semibold">{formatDate(selectedDate)}</div>
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                <div className="p-2 bg-light rounded">
                                                    <small className="text-muted">Status</small>
                                                    <div>
                                                        <Badge color={selectedAttendance?.status ? getStatusColor(selectedAttendance.status) : 'secondary'}>
                                                            {selectedAttendance?.status ? getStatusLabel(selectedAttendance.status) : 'Unknown'}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>

                                        {/* ✅ Show attendance details if there are hours OR if it's no_out_time with check-in */}
                                        {selectedAttendance && (
                                            (selectedAttendance.hours !== null && selectedAttendance.hours > 0) ||
                                            (selectedAttendance.status === 'no_out_time' && selectedAttendance.check_in)
                                        ) && (
                                                <>
                                                    <Row className="g-2 mt-2">
                                                        <Col md={4}>
                                                            <div className="p-2 bg-light rounded">
                                                                <small className="text-muted">Total Hours</small>
                                                                <div className="fw-semibold">
                                                                    {selectedAttendance.hours !== null ? `${selectedAttendance.hours.toFixed(1)} hrs` : '0 hrs'}
                                                                </div>
                                                            </div>
                                                        </Col>
                                                        <Col md={4}>
                                                            <div className="p-2 bg-light rounded">
                                                                <small className="text-muted">Regular Hours</small>
                                                                <div className="fw-semibold">{selectedAttendance.regular_hours?.toFixed(1) || '0'} hrs</div>
                                                            </div>
                                                        </Col>
                                                        <Col md={4}>
                                                            <div className="p-2 bg-light rounded">
                                                                <small className="text-muted">Overtime</small>
                                                                <div className="fw-semibold text-success">
                                                                    {selectedAttendance.overtime_hours?.toFixed(1) || '0'} hrs
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    </Row>

                                                    {/* ✅ Show check-in/out - ALWAYS show check-in if available */}
                                                    {selectedAttendance.check_in || (selectedAttendance.sessions && selectedAttendance.sessions.length > 0) ? (
                                                        <Row className="g-2 mt-2">
                                                            <Col md={6}>
                                                                <div className="p-2 bg-light rounded">
                                                                    <small className="text-muted">Check In</small>
                                                                    <div className="fw-semibold">
                                                                        {selectedAttendance.sessions && selectedAttendance.sessions.length > 0
                                                                            ? formatTo12Hour(selectedAttendance.sessions[0].in)
                                                                            : selectedAttendance.check_in
                                                                                ? formatTo12Hour(selectedAttendance.check_in)
                                                                                : '-'}
                                                                    </div>
                                                                    {/* ✅ Show check-in office name */}
                                                                    {selectedAttendance.check_in_office_name && (
                                                                        <div className="mt-1">
                                                                            <Badge color="info" className="me-1">
                                                                                <Clock size={10} className="me-1" />
                                                                                Office
                                                                            </Badge>
                                                                            <span className="text-muted small">{selectedAttendance.check_in_office_name}</span>
                                                                            {selectedAttendance.check_in_office_address && (
                                                                                <span className="text-muted small d-block" style={{ fontSize: '10px' }}>
                                                                                    📍 {selectedAttendance.check_in_office_address}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                    {/* ✅ Show check-in photo if available */}
                                                                    {selectedAttendance.check_in_photo && (
                                                                        <div className="mt-1">
                                                                            <img
                                                                                src={getSafeImageUrl(selectedAttendance.check_in_photo)}
                                                                                alt="Check-in"
                                                                                style={{
                                                                                    width: '80px',
                                                                                    height: '80px',
                                                                                    objectFit: 'cover',
                                                                                    borderRadius: '8px',
                                                                                    border: '1px solid #000000'
                                                                                }}
                                                                                onError={(e) => {
                                                                                    console.warn('Failed to load check-in image:', selectedAttendance.check_in_photo);
                                                                                    const img = e.target as HTMLImageElement;
                                                                                    img.style.display = 'none';
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                    {/* ✅ Show "No check-in" badge if missing */}
                                                                    {!selectedAttendance.check_in && (
                                                                        <div className="mt-1">
                                                                            <Badge color="danger">No Check-in</Badge>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </Col>
                                                            <Col md={6}>
                                                                <div className="p-2 bg-light rounded">
                                                                    <small className="text-muted">Check Out</small>
                                                                    <div className="fw-semibold">
                                                                        {selectedAttendance.sessions && selectedAttendance.sessions.length > 0
                                                                            ? formatTo12Hour(selectedAttendance.sessions[selectedAttendance.sessions.length - 1].out)
                                                                            : selectedAttendance.check_out
                                                                                ? formatTo12Hour(selectedAttendance.check_out)
                                                                                : selectedAttendance.status === 'no_out_time'
                                                                                    ? '⚠️ Not Checked Out'
                                                                                    : '-'}
                                                                    </div>
                                                                    {selectedAttendance.check_out_office_name && (
                                                                        <div className="mt-1">
                                                                            <Badge color="info" className="me-1">
                                                                                <Clock size={10} className="me-1" />
                                                                                Office
                                                                            </Badge>
                                                                            <span className="text-muted small">{selectedAttendance.check_out_office_name}</span>
                                                                            {selectedAttendance.check_out_office_address && (
                                                                                <span className="text-muted small d-block" style={{ fontSize: '10px' }}>
                                                                                    📍 {selectedAttendance.check_out_office_address}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                    {/* ✅ Show different office warning */}
                                                                    {selectedAttendance.is_different_office && (
                                                                        <div className="mt-1">
                                                                            <Badge color="warning">🔄 Different Office</Badge>
                                                                        </div>
                                                                    )}
                                                                    {/* ✅ Show check-out photo if available */}
                                                                    {selectedAttendance.check_out_photo && (
                                                                        <div className="mt-1">
                                                                            <img
                                                                                src={getSafeImageUrl(selectedAttendance.check_out_photo)}
                                                                                alt="Check-out"
                                                                                style={{
                                                                                    width: '80px',
                                                                                    height: '80px',
                                                                                    objectFit: 'cover',
                                                                                    borderRadius: '8px',
                                                                                    border: '1px solid #000000'
                                                                                }}
                                                                                onError={(e) => {
                                                                                    console.warn('Failed to load check-out image:', selectedAttendance.check_out_photo);
                                                                                    const img = e.target as HTMLImageElement;
                                                                                    img.style.display = 'none';
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                    {/* ✅ Show "No check-out" warning for no_out_time */}
                                                                    {selectedAttendance.status === 'no_out_time' && (
                                                                        <div className="mt-1">
                                                                            <Badge color="warning">⚠️ No Check-out</Badge>
                                                                        </div>
                                                                    )}
                                                                    {/* ✅ Show checkout notes if available */}
                                                                    {selectedAttendance.check_out_notes && (
                                                                        <div className="mt-2" style={{
                                                                            backgroundColor: '#fff8e1',
                                                                            borderRadius: '8px',
                                                                            border: '1px solid #ffd54f',
                                                                            padding: '10px 12px',
                                                                            position: 'relative'
                                                                        }}>
                                                                            <div className="d-flex align-items-start gap-2">
                                                                                <div style={{
                                                                                    backgroundColor: '#ffb300',
                                                                                    borderRadius: '4px',
                                                                                    padding: '4px 8px',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: '4px',
                                                                                    flexShrink: 0,
                                                                                    marginTop: '2px'
                                                                                }}>
                                                                                    <FileText size={12} color="#fff" />
                                                                                    <span style={{
                                                                                        color: '#fff',
                                                                                        fontSize: '11px',
                                                                                        fontWeight: '600',
                                                                                        letterSpacing: '0.3px'
                                                                                    }}>
                                                                                        Note
                                                                                    </span>
                                                                                </div>
                                                                                <span style={{
                                                                                    fontSize: '13px',
                                                                                    color: '#424242',
                                                                                    whiteSpace: 'pre-wrap',
                                                                                    wordBreak: 'break-word',
                                                                                    lineHeight: '1.6',
                                                                                    flex: 1
                                                                                }}>
                                                                                    {selectedAttendance.check_out_notes}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    ) : null}

                                                    {/* ✅ NEW: Location Map Section */}
                                                    {(selectedAttendance.check_in_location?.latitude || selectedAttendance.check_out_location?.latitude) && (
                                                        <div className="mt-2">
                                                            <div className="p-2 bg-light rounded">
                                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                                    <small className="text-muted">📍 Location</small>
                                                                    <div>
                                                                        {selectedAttendance.check_in_location?.latitude && (
                                                                            <Badge color="success" className="me-1">Check-in</Badge>
                                                                        )}
                                                                        {selectedAttendance.check_out_location?.latitude && (
                                                                            <Badge color="danger">Check-out</Badge>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <LocationSection
                                                                    checkInLocation={selectedAttendance.check_in_location}
                                                                    checkOutLocation={selectedAttendance.check_out_location}
                                                                    checkInTime={formatTo12Hour(selectedAttendance.check_in)}
                                                                    checkOutTime={formatTo12Hour(selectedAttendance.check_out)}
                                                                    showRadius={false}  // Optional: show radius circle
                                                                    radius={100}       // Optional: radius in meters
                                                                />
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Show sessions only if available */}
                                                    {selectedAttendance.sessions && selectedAttendance.sessions.length > 0 && !selectedAttendance.is_manual && (
                                                        <div className="mt-2">
                                                            <small className="text-muted">Sessions</small>
                                                            <div className="bg-light rounded p-2 mt-1">
                                                                {selectedAttendance.sessions.map((session, idx) => (
                                                                    <div key={idx} className="small">
                                                                        {formatTo12Hour(session.in)} → {formatTo12Hour(session.out)} ({session.duration.toFixed(1)} hrs)
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* ✅ Holiday badge with attendance */}
                                                    {selectedAttendance.is_holiday && (
                                                        <div className="mt-2 p-2 bg-primary bg-opacity-10 rounded border border-primary">
                                                            <span className="fw-semibold text-primary">📅 Holiday (Worked on Holiday)</span>
                                                            {selectedAttendance.holiday_name && (
                                                                <span className="ms-2 text-muted small">- {selectedAttendance.holiday_name}</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                        {/* ✅ Show holiday without attendance */}
                                        {selectedAttendance?.is_holiday && (!selectedAttendance.hours || selectedAttendance.hours === 0) && (
                                            <div className="mt-2 p-2 bg-primary bg-opacity-10 rounded">
                                                <span className="fw-semibold">📅 Holiday</span>
                                                {selectedAttendance.holiday_name && (
                                                    <span className="ms-2 text-muted small">- {selectedAttendance.holiday_name}</span>
                                                )}
                                            </div>
                                        )}

                                        {/* Show absent status */}
                                        {selectedAttendance?.status === 'absent' && !selectedAttendance.check_in && !selectedAttendance.is_holiday && (
                                            <div className="mt-2 p-2 bg-danger bg-opacity-10 rounded">
                                                <span className="fw-semibold">❌ No punches recorded</span>
                                            </div>
                                        )}

                                        {/* Show no out time status with check-in details */}
                                        {selectedAttendance?.status === 'no_out_time' && selectedAttendance.check_in && (
                                            <div className="mt-2 p-2 bg-warning bg-opacity-10 rounded">
                                                <span className="fw-semibold">⚠️ Checked In: {formatTo12Hour(selectedAttendance.check_in)}</span>
                                                <br />
                                                <small className="text-muted">No check-out recorded for this day</small>
                                            </div>
                                        )}
                                    </ModalBody>
                                    <ModalFooter>
                                        <Button color="secondary" onClick={() => setViewModal(false)}>
                                            Close
                                        </Button>
                                        {selectedAttendance &&
                                            !selectedAttendance.is_half_day &&
                                            selectedAttendance.status !== "half_day" &&
                                            selectedAttendance.status !== "absent" &&
                                            !selectedAttendance.is_holiday &&
                                            !selectedAttendance.lop && (
                                                <Button color="info" onClick={handleOpenHalfDay}>
                                                    <Clock size={14} className="me-1" />
                                                    Mark Half Day
                                                </Button>
                                            )}
                                        {selectedAttendance && !selectedAttendance.lop && (
                                            <Button
                                                color="danger"
                                                onClick={() => handleOpenLop()}
                                                style={{
                                                    backgroundColor: '#8b0000',
                                                    borderColor: '#8b0000',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                🚫 Mark as Loss of Pay
                                            </Button>
                                        )}
                                        {selectedAttendance?.lop && (
                                            <>
                                                <Button
                                                    color="warning"
                                                    onClick={() =>
                                                        handleOpenLop(
                                                            selectedAttendance.lop!.id,
                                                            selectedAttendance.lop!.notes
                                                        )
                                                    }
                                                >
                                                    <Edit2 size={14} className="me-1" /> Edit LOP
                                                </Button>
                                                <Button
                                                    onClick={() =>
                                                        selectedAttendance.lop && handleDeactivateLop(selectedAttendance.lop.id)
                                                    }
                                                    style={{
                                                        backgroundColor: '#8b0000',
                                                        borderColor: '#8b0000',
                                                        color: '#fff',
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    <X size={14} className="me-1" /> Remove LOP
                                                </Button>
                                            </>
                                        )}

                                        {/* ✅ Show edit buttons for holidays with attendance, regular days with attendance, and no_out_time */}
                                        {selectedAttendance && !selectedAttendance.lop && (
                                            (selectedAttendance.is_holiday && selectedAttendance.hours !== null && selectedAttendance.hours > 0) ||
                                            (!selectedAttendance.is_holiday && selectedAttendance.status !== 'absent') ||
                                            selectedAttendance.status === 'no_out_time'
                                        ) && (
                                                <>
                                                    <Button color="warning" onClick={handleMarkAbsent}>
                                                        <UserX size={14} className="me-1" />
                                                        Mark Absent
                                                    </Button>
                                                    <Button color="primary" onClick={handleEdit}>
                                                        <Edit2 size={14} className="me-1" />
                                                        {selectedAttendance.is_holiday ? 'Edit Holiday Attendance' : 'Manual Attendance'}
                                                    </Button>
                                                </>
                                            )}

                                        {selectedAttendance?.is_holiday &&
                                            (!selectedAttendance.hours || selectedAttendance.hours === 0) &&
                                            !selectedAttendance.lop && (
                                                <Button color="primary" onClick={handleEdit}>
                                                    <Edit2 size={14} className="me-1" />
                                                    Add Holiday Attendance
                                                </Button>
                                            )}
                                    </ModalFooter>
                                </Modal>
                                {/* Edit Attendance Modal */}
                                <Modal isOpen={editModal} toggle={() => setEditModal(false)} size="md">
                                    <ModalHeader toggle={() => setEditModal(false)}>
                                        <Edit2 size={18} className="me-2" />
                                        Edit Attendance
                                    </ModalHeader>
                                    <ModalBody>
                                        <Form>
                                            <Row className="g-2">
                                                <Col md={6}>
                                                    <div className="p-2 bg-light rounded">
                                                        <small className="text-muted">Employee</small>
                                                        <div className="fw-semibold">
                                                            {selectedEmployee?.name}
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col md={6}>
                                                    <div className="p-2 bg-light rounded">
                                                        <small className="text-muted">Date</small>
                                                        <div className="fw-semibold">{formatDate(editData.date)}</div>
                                                    </div>
                                                </Col>
                                            </Row>

                                            <Row className="g-2 mt-2">
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="form-label-sm">Punch IN <span className="text-danger">*</span></Label>
                                                        <Input
                                                            type="time"
                                                            value={editData.punch_in}
                                                            onChange={(e) => setEditData(prev => ({ ...prev, punch_in: e.target.value }))}
                                                            bsSize="sm"
                                                            required
                                                        />
                                                        <small className="text-muted">e.g., 09:00</small>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="form-label-sm">Punch OUT <span className="text-danger">*</span></Label>
                                                        <Input
                                                            type="time"
                                                            value={editData.punch_out}
                                                            onChange={(e) => setEditData(prev => ({ ...prev, punch_out: e.target.value }))}
                                                            bsSize="sm"
                                                            required
                                                        />
                                                        <small className="text-muted">e.g., 18:00</small>
                                                    </FormGroup>
                                                </Col>
                                            </Row>

                                            {/* ✅ New Notes Field - Required */}
                                            <Row className="mt-2">
                                                <Col md={12}>
                                                    <FormGroup>
                                                        <Label className="form-label-sm">
                                                            Notes <span className="text-danger">*</span>
                                                        </Label>
                                                        <Input
                                                            type="textarea"
                                                            value={editData.notes || ''}
                                                            onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                                                            bsSize="sm"
                                                            rows={3}
                                                            placeholder="Please provide reason for editing attendance..."
                                                            required
                                                        />
                                                        <small className="text-muted">
                                                            Provide a reason for this edit (e.g., "Forgot to check in", "Manual correction", etc.)
                                                        </small>
                                                    </FormGroup>
                                                </Col>
                                            </Row>

                                            <Alert color="info" className="mt-2 mb-0">
                                                <small>
                                                    <i className="ri-information-line me-1"></i>
                                                    This will update all punches for this day. Previous punches will be replaced.
                                                </small>
                                            </Alert>
                                        </Form>
                                    </ModalBody>
                                    <ModalFooter>
                                        <Button color="secondary" onClick={() => setEditModal(false)}>
                                            Cancel
                                        </Button>
                                        <Button
                                            color="primary"
                                            onClick={handleSaveEdit}
                                            disabled={saving || !editData.notes?.trim()}
                                        >
                                            {saving ? (
                                                <>
                                                    <Spinner size="sm" className="me-2" />
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <Save size={14} className="me-1" />
                                                    Save Changes
                                                </>
                                            )}
                                        </Button>
                                    </ModalFooter>
                                </Modal>

                                {/* ---------- Leave Details Modal ---------- */}
                                <Modal
                                    isOpen={leaveDetailModal}
                                    toggle={() => {
                                        if (leaveRemoving) return;
                                        setLeaveDetailModal(false);
                                        setLeaveDetail(null);
                                        setLeaveDetailEmployee(null);
                                    }}
                                    size="md"
                                >
                                    <ModalHeader
                                        toggle={() => {
                                            setLeaveDetailModal(false);
                                            setLeaveDetail(null);
                                            setLeaveDetailEmployee(null);
                                        }}
                                    >
                                        <Calendar size={18} className="me-2" />
                                        Leave Details
                                    </ModalHeader>

                                    <ModalBody>
                                        {leaveDetail && leaveDetailEmployee && (
                                            <>
                                                {/* Employee + Date */}
                                                <Row className="g-2 mb-3">
                                                    <Col md={6}>
                                                        <div className="p-2 bg-light rounded">
                                                            <small className="text-muted">Employee</small>
                                                            <div className="fw-semibold">
                                                                {leaveDetailEmployee.name}
                                                            </div>
                                                            <small className="text-muted">
                                                                {leaveDetailEmployee.employee_code}
                                                            </small>
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="p-2 bg-light rounded">
                                                            <small className="text-muted">Date</small>
                                                            <div className="fw-semibold">
                                                                {formatDate(leaveDetailDate)}
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>

                                                {/* Leave Type with color */}
                                                <Row className="g-2 mb-3">
                                                    <Col md={12}>
                                                        <div className="p-2 bg-light rounded d-flex align-items-center gap-2">
                                                            <span
                                                                style={{
                                                                    display: 'inline-block',
                                                                    width: 14,
                                                                    height: 14,
                                                                    borderRadius: 3,
                                                                    backgroundColor:
                                                                        leaveDetail.color_hex || '#6c757d',
                                                                    border: '1px solid rgba(0,0,0,0.15)',
                                                                }}
                                                            />
                                                            <div>
                                                                <small className="text-muted d-block">Leave Type</small>
                                                                <span className="fw-semibold">
                                                                    {leaveDetail.leave_type_name || '—'}
                                                                </span>
                                                                {leaveDetail.leave_type_short && (
                                                                    <span className="text-muted ms-2">
                                                                        ({leaveDetail.leave_type_short})
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>

                                                {/* Paid / Unpaid + Duration */}
                                                <Row className="g-2 mb-3">
                                                    <Col md={6}>
                                                        <div className="p-2 bg-light rounded">
                                                            <small className="text-muted">Payment</small>
                                                            <div>
                                                                {leaveDetail.is_paid ? (
                                                                    <Badge color="primary">Paid</Badge>
                                                                ) : (
                                                                    <Badge color="secondary">Unpaid</Badge>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="p-2 bg-light rounded">
                                                            <small className="text-muted">Duration</small>
                                                            <div className="fw-semibold">
                                                                {leaveDetail.half_day_type != null
                                                                    ? 'Half Day (0.5)'
                                                                    : `${leaveDetail.number_of_days} ${leaveDetail.number_of_days === 1
                                                                        ? 'Day'
                                                                        : 'Days'
                                                                    }`}
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>

                                                {/* Reason */}
                                                <Row className="g-2">
                                                    <Col md={12}>
                                                        <div className="p-2 bg-light rounded">
                                                            <small className="text-muted">Reason</small>
                                                            <div style={{ whiteSpace: 'pre-wrap' }}>
                                                                {leaveDetail.reason || '—'}
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>

                                                {/* Leave Request ID (subtle) */}
                                                <div className="mt-3 text-end">
                                                    <small className="text-muted">
                                                        Request #{leaveDetail.leave_request_id}
                                                    </small>
                                                </div>
                                            </>
                                        )}
                                    </ModalBody>

                                    <ModalFooter>
                                        <Button
                                            color="secondary"
                                            onClick={() => {
                                                setLeaveDetailModal(false);
                                                setLeaveDetail(null);
                                                setLeaveDetailEmployee(null);
                                                setLeaveRemoveError(null);
                                            }}
                                            disabled={leaveRemoving}
                                        >
                                            Close
                                        </Button>

                                        {/* ✅ Remove (cancel) leave — same UX as LOP remove */}
                                        {leaveDetail?.leave_request_id && (
                                            <Button
                                                color="danger"
                                                onClick={handleRemoveLeave}
                                                disabled={leaveRemoving}
                                                style={{
                                                    backgroundColor: '#8b0000',
                                                    borderColor: '#8b0000',
                                                    color: '#fff',
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {leaveRemoving ? (
                                                    <>
                                                        <span
                                                            className="spinner-border spinner-border-sm me-2"
                                                            role="status"
                                                            aria-hidden="true"
                                                        />
                                                        Removing…
                                                    </>
                                                ) : (
                                                    <>
                                                        <X size={14} className="me-1" />
                                                        Remove Leave
                                                    </>
                                                )}
                                            </Button>
                                        )}
                                    </ModalFooter>
                                </Modal>

                                {/* Add Attendance / Leave Modal */}
                                <Modal
                                    isOpen={addModal}
                                    toggle={() => { setAddModal(false); resetLeaveForm(); }}
                                    size="lg"
                                >
                                    <ModalHeader toggle={() => { setAddModal(false); resetLeaveForm(); }}>
                                        <Plus size={18} className="me-2" />
                                        Add Attendance / Leave
                                    </ModalHeader>
                                    <ModalBody>
                                        {selectedAttendance?.lop && (
                                            <div
                                                className="mb-3 p-3 rounded d-flex align-items-start gap-2"
                                                style={{
                                                    background: 'linear-gradient(135deg, #fff5f5, #ffe5e5)',
                                                    border: '2px solid #8b0000',
                                                }}
                                            >
                                                <span style={{ fontSize: '22px', lineHeight: 1 }}>🚫</span>
                                                <div className="flex-grow-1">
                                                    <div className="d-flex align-items-center gap-2 mb-1">
                                                        <span
                                                            style={{
                                                                backgroundColor: '#8b0000',
                                                                color: '#fff',
                                                                fontSize: '0.65rem',
                                                                fontWeight: '800',
                                                                padding: '2px 8px',
                                                                borderRadius: '4px',
                                                                border: '1.5px solid #ffd700',
                                                                letterSpacing: '0.05em',
                                                            }}
                                                        >
                                                            LOSS OF PAY
                                                        </span>
                                                        <small className="text-muted">
                                                            Salary for this day will be deducted
                                                        </small>
                                                    </div>
                                                    <div
                                                        className="small"
                                                        style={{ color: '#4a0000', whiteSpace: 'pre-wrap' }}
                                                    >
                                                        <strong>Reason:</strong> {selectedAttendance.lop.notes}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {/* ---------- Tabs ---------- */}
                                        <Nav tabs className="mb-3">
                                            <NavItem>
                                                <NavLink
                                                    className={addActiveTab === 'attendance' ? 'active' : ''}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => setAddActiveTab('attendance')}
                                                >
                                                    <Clock size={14} className="me-1" />
                                                    Attendance
                                                </NavLink>
                                            </NavItem>
                                            <NavItem>
                                                <NavLink
                                                    className={addActiveTab === 'leave' ? 'active' : ''}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => setAddActiveTab('leave')}
                                                >
                                                    <FileText size={14} className="me-1" />
                                                    Leave
                                                </NavLink>
                                            </NavItem>
                                        </Nav>

                                        {/* Employee + Date — common to both tabs */}
                                        <Row className="g-2 mb-3">
                                            <Col md={6}>
                                                <div className="p-2 bg-light rounded">
                                                    <small className="text-muted">Employee</small>
                                                    <div className="fw-semibold">{selectedEmployee?.name}</div>
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                <div className="p-2 bg-light rounded">
                                                    <small className="text-muted">Date</small>
                                                    <div className="fw-semibold">{formatDate(addData.date)}</div>
                                                </div>
                                            </Col>
                                        </Row>

                                        <TabContent activeTab={addActiveTab}>

                                            {/* ================= ATTENDANCE TAB (existing content) ================= */}
                                            <TabPane tabId="attendance">
                                                {!selectedAttendance?.lop && (
                                                    <Alert color="warning" className="mb-3">
                                                        <AlertCircle size={16} className="me-2" />
                                                        No attendance record found for this day. Add the punch details below.
                                                    </Alert>
                                                )}

                                                {selectedAttendance?.lop && (
                                                    <Alert color="danger" className="mb-3">
                                                        <AlertCircle size={16} className="me-2" />
                                                        This day is marked as <strong>Loss of Pay</strong>. Remove the LOP
                                                        first if you want to add attendance.
                                                    </Alert>
                                                )}

                                                <Form>
                                                    <Row className="g-2">
                                                        <Col md={6}>
                                                            <FormGroup>
                                                                <Label className="form-label-sm">
                                                                    Punch IN <span className="text-danger">*</span>
                                                                </Label>
                                                                <Input
                                                                    type="time"
                                                                    value={addData.punch_in}
                                                                    onChange={e =>
                                                                        setAddData(prev => ({ ...prev, punch_in: e.target.value }))
                                                                    }
                                                                    bsSize="sm"
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                        <Col md={6}>
                                                            <FormGroup>
                                                                <Label className="form-label-sm">
                                                                    Punch OUT <span className="text-danger">*</span>
                                                                </Label>
                                                                <Input
                                                                    type="time"
                                                                    value={addData.punch_out}
                                                                    onChange={e =>
                                                                        setAddData(prev => ({ ...prev, punch_out: e.target.value }))
                                                                    }
                                                                    bsSize="sm"
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>

                                                    <Row className="mt-2">
                                                        <Col md={12}>
                                                            <FormGroup>
                                                                <Label className="form-label-sm">
                                                                    Notes <span className="text-danger">*</span>
                                                                </Label>
                                                                <Input
                                                                    type="textarea"
                                                                    value={addData.notes || ''}
                                                                    onChange={e =>
                                                                        setAddData(prev => ({ ...prev, notes: e.target.value }))
                                                                    }
                                                                    bsSize="sm"
                                                                    rows={3}
                                                                    placeholder="Please provide reason for adding attendance..."
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>
                                                </Form>
                                            </TabPane>

                                            {/* ================= LEAVE TAB (new) ================= */}
                                            <TabPane tabId="leave">
                                                {leaveLoading ? (
                                                    <div className="text-center py-4">
                                                        <Spinner color="primary" className="me-2" />
                                                        <span>Loading leave balance...</span>
                                                    </div>
                                                ) : leaveError ? (
                                                    <Alert color="danger">{leaveError}</Alert>
                                                ) : leaveBalances.length === 0 ? (
                                                    <Alert color="warning">
                                                        No leave policy assigned to this employee.
                                                    </Alert>
                                                ) : (
                                                    <>
                                                        {/* Balance summary table */}
                                                        <div className="table-responsive mb-3">
                                                            <Table bordered size="sm" className="mb-0">
                                                                <thead className="table-light">
                                                                    <tr>
                                                                        <th>Leave Type</th>
                                                                        <th className="text-center">Allocated</th>
                                                                        <th className="text-center">Used</th>
                                                                        <th className="text-center">Available</th>
                                                                        <th className="text-center">Paid</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {leaveBalances.map(b => (
                                                                        <tr key={b.leave_type_id}>
                                                                            <td>
                                                                                <span
                                                                                    className="me-2"
                                                                                    style={{
                                                                                        display: 'inline-block',
                                                                                        width: 10,
                                                                                        height: 10,
                                                                                        borderRadius: 2,
                                                                                        backgroundColor:
                                                                                            b.color_hex || '#6c757d',
                                                                                    }}
                                                                                />
                                                                                {b.leave_type_name}
                                                                            </td>
                                                                            <td className="text-center">{b.allocated}</td>
                                                                            <td className="text-center">{b.used}</td>
                                                                            <td className="text-center fw-semibold">
                                                                                {b.available}
                                                                            </td>
                                                                            <td className="text-center">
                                                                                {b.is_paid ? (
                                                                                    <Badge color="primary">Paid</Badge>
                                                                                ) : (
                                                                                    <Badge color="secondary">Unpaid</Badge>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </Table>
                                                        </div>

                                                        {/* Leave request form */}
                                                        <Form>
                                                            <Row className="g-2">
                                                                <Col md={6}>
                                                                    <FormGroup>
                                                                        <Label className="form-label-sm">
                                                                            Leave Type <span className="text-danger">*</span>
                                                                        </Label>
                                                                        <Input
                                                                            type="select"
                                                                            bsSize="sm"
                                                                            value={leaveFormData.leave_type_id || ''}
                                                                            onChange={e =>
                                                                                handleLeaveTypeChange(Number(e.target.value))
                                                                            }
                                                                        >
                                                                            <option value="">-- Select --</option>
                                                                            {leaveBalances.map(b => (
                                                                                <option
                                                                                    key={b.leave_type_id}
                                                                                    value={b.leave_type_id}
                                                                                >
                                                                                    {b.leave_type_name} ({b.available} left)
                                                                                </option>
                                                                            ))}
                                                                        </Input>
                                                                    </FormGroup>
                                                                </Col>
                                                                <Col md={6}>
                                                                    <FormGroup>
                                                                        <Label className="form-label-sm">
                                                                            Paid / Unpaid
                                                                        </Label>
                                                                        <Input
                                                                            type="select"
                                                                            bsSize="sm"
                                                                            value={leaveFormData.is_paid ? '1' : '0'}
                                                                            onChange={e =>
                                                                                setLeaveFormData(prev => ({
                                                                                    ...prev,
                                                                                    is_paid: e.target.value === '1',
                                                                                }))
                                                                            }
                                                                        >
                                                                            <option value="1">Paid</option>
                                                                            <option value="0">Unpaid</option>
                                                                        </Input>
                                                                    </FormGroup>
                                                                </Col>

                                                                {/* <Col md={6}>
                                                                    <FormGroup>
                                                                        <Label className="form-label-sm">
                                                                            From Date <span className="text-danger">*</span>
                                                                        </Label>
                                                                        <Input
                                                                            type="date"
                                                                            bsSize="sm"
                                                                            value={leaveFormData.from_date}
                                                                            onChange={e =>
                                                                                setLeaveFormData(prev => ({
                                                                                    ...prev,
                                                                                    from_date: e.target.value,
                                                                                }))
                                                                            }
                                                                        />
                                                                    </FormGroup>
                                                                </Col>
                                                                <Col md={6}>
                                                                    <FormGroup>
                                                                        <Label className="form-label-sm">
                                                                            To Date <span className="text-danger">*</span>
                                                                        </Label>
                                                                        <Input
                                                                            type="date"
                                                                            bsSize="sm"
                                                                            value={leaveFormData.to_date}
                                                                            onChange={e =>
                                                                                setLeaveFormData(prev => ({
                                                                                    ...prev,
                                                                                    to_date: e.target.value,
                                                                                }))
                                                                            }
                                                                        />
                                                                    </FormGroup>
                                                                </Col> */}

                                                                <Col md={12}>
                                                                    <FormGroup check className="mb-0">
                                                                        <Label check>
                                                                            <Input
                                                                                type="checkbox"
                                                                                checked={leaveFormData.half_day}
                                                                                onChange={e =>
                                                                                    setLeaveFormData(prev => ({
                                                                                        ...prev,
                                                                                        half_day: e.target.checked,
                                                                                    }))
                                                                                }
                                                                            />
                                                                            <span className="ms-2">Half Day</span>
                                                                        </Label>
                                                                    </FormGroup>
                                                                </Col>

                                                                <Col md={12}>
                                                                    <FormGroup>
                                                                        <Label className="form-label-sm">
                                                                            Reason <span className="text-danger">*</span>
                                                                        </Label>
                                                                        <Input
                                                                            type="textarea"
                                                                            bsSize="sm"
                                                                            rows={3}
                                                                            value={leaveFormData.reason}
                                                                            onChange={e =>
                                                                                setLeaveFormData(prev => ({
                                                                                    ...prev,
                                                                                    reason: e.target.value,
                                                                                }))
                                                                            }
                                                                            placeholder="Reason for leave..."
                                                                        />
                                                                    </FormGroup>
                                                                </Col>
                                                            </Row>

                                                            {leaveError && (
                                                                <Alert color="danger" className="mt-2 mb-0">
                                                                    {leaveError}
                                                                </Alert>
                                                            )}
                                                        </Form>
                                                    </>
                                                )}
                                            </TabPane>
                                        </TabContent>
                                    </ModalBody>

                                    <ModalFooter>
                                        <Button
                                            color="secondary"
                                            onClick={() => { setAddModal(false); resetLeaveForm(); }}
                                        >
                                            Cancel
                                        </Button>

                                        {addActiveTab === 'attendance' ? (
                                            <>
                                                {selectedAttendance?.lop ? (
                                                    <>
                                                        <Button
                                                            color="warning"
                                                            onClick={() =>
                                                                handleOpenLop(
                                                                    selectedAttendance.lop!.id,
                                                                    selectedAttendance.lop!.notes
                                                                )
                                                            }
                                                        >
                                                            <Edit2 size={14} className="me-1" /> Edit LOP
                                                        </Button>
                                                        <Button
                                                            onClick={() =>
                                                                selectedAttendance.lop &&
                                                                handleDeactivateLop(selectedAttendance.lop.id)
                                                            }
                                                            style={{
                                                                backgroundColor: '#8b0000',
                                                                borderColor: '#8b0000',
                                                                color: '#fff',
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            <X size={14} className="me-1" /> Remove LOP
                                                        </Button>
                                                    </>
                                                ) : (
                                                    /* ✅ Otherwise → allow marking LOP */
                                                    <Button
                                                        color="danger"
                                                        onClick={() => handleOpenLop()}
                                                        disabled={adding}
                                                        style={{
                                                            backgroundColor: '#8b0000',
                                                            borderColor: '#8b0000',
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        🚫 Mark as Loss of Pay
                                                    </Button>
                                                )}

                                                {/* Add Attendance — hide when LOP exists (they can't add punches
          to a day that's already been LOP'd; must remove LOP first) */}
                                                {!selectedAttendance?.lop && (
                                                    <Button
                                                        color="primary"
                                                        onClick={handleAddAttendance}
                                                        disabled={adding || !addData.notes?.trim()}
                                                    >
                                                        {adding ? (
                                                            <>
                                                                <Spinner size="sm" className="me-2" /> Adding...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Plus size={14} className="me-1" /> Add Attendance
                                                            </>
                                                        )}
                                                    </Button>
                                                )}
                                            </>
                                        ) : (
                                            <Button
                                                color="primary"
                                                onClick={handleSubmitLeave}
                                                disabled={leaveSubmitting || leaveLoading}
                                            >
                                                {leaveSubmitting ? (
                                                    <>
                                                        <Spinner size="sm" className="me-2" /> Applying...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FileText size={14} className="me-1" /> Apply Leave
                                                    </>
                                                )}
                                            </Button>

                                        )}
                                    </ModalFooter>
                                </Modal>

                                {/* ─── Half Day confirmation modal ─── */}
                                <Modal isOpen={halfDayModal} toggle={() => !halfDaySubmitting && setHalfDayModal(false)} size="md">
                                    <ModalHeader toggle={() => !halfDaySubmitting && setHalfDayModal(false)}>
                                        <div className="d-flex align-items-center">
                                            <Clock size={18} className="me-2 text-info" />
                                            <div>
                                                <div className="fw-semibold">Mark as Half Day</div>
                                                <small className="text-muted">
                                                    {selectedEmployee?.name} • {formatDate(selectedDate)}
                                                </small>
                                            </div>
                                        </div>
                                    </ModalHeader>

                                    <ModalBody>
                                        <div className="mb-3 p-2 bg-warning bg-opacity-10 rounded border border-warning">
                                            <div className="d-flex align-items-start gap-2">
                                                <span style={{ fontSize: '18px' }}>⚠️</span>
                                                <div className="small">
                                                    <div className="fw-semibold text-warning-emphasis">
                                                        This overrides the hours-based status.
                                                    </div>
                                                    <div className="text-muted mt-1">
                                                        Even if the employee worked a full shift, this day will count as <strong>half day (0.5)</strong> in salary calculations.
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mb-2">
                                            <label htmlFor="halfDayNotes" className="form-label fw-semibold small">
                                                Reason / Notes <span className="text-danger">*</span>
                                            </label>
                                            <textarea
                                                id="halfDayNotes"
                                                className="form-control"
                                                rows={3}
                                                placeholder="e.g. Late 4 times this week — docking as half day"
                                                value={halfDayNotes}
                                                onChange={(e) => setHalfDayNotes(e.target.value)}
                                                disabled={halfDaySubmitting}
                                                autoFocus
                                            />
                                            <small className="text-muted">
                                                This note will be attached to every punch on this day for audit purposes.
                                            </small>
                                        </div>

                                        {halfDayError && (
                                            <div className="alert alert-danger py-2 mb-0 small">{halfDayError}</div>
                                        )}
                                    </ModalBody>

                                    <ModalFooter>
                                        <Button
                                            color="secondary"
                                            onClick={() => setHalfDayModal(false)}
                                            disabled={halfDaySubmitting}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            color="info"
                                            onClick={handleSubmitHalfDay}
                                            disabled={halfDaySubmitting || !halfDayNotes.trim()}
                                        >
                                            {halfDaySubmitting ? (
                                                <>
                                                    <span
                                                        className="spinner-border spinner-border-sm me-2"
                                                        role="status"
                                                        aria-hidden="true"
                                                    />
                                                    Marking…
                                                </>
                                            ) : (
                                                <>
                                                    <Clock size={14} className="me-1" />
                                                    Confirm Half Day
                                                </>
                                            )}
                                        </Button>
                                    </ModalFooter>
                                </Modal>
                                {/* Mark Absent Modal */}
                                <Modal isOpen={absentModal} toggle={() => setAbsentModal(false)} size="md">
                                    <ModalHeader toggle={() => setAbsentModal(false)}>
                                        <UserX size={18} className="me-2" />
                                        Mark Absent
                                    </ModalHeader>
                                    <ModalBody>
                                        <Alert color="warning">
                                            <AlertCircle size={16} className="me-2" />
                                            This will remove all punches for this day and mark the employee as absent.
                                        </Alert>
                                        <Row className="g-2">
                                            <Col md={6}>
                                                <div className="p-2 bg-light rounded">
                                                    <small className="text-muted">Employee</small>
                                                    <div className="fw-semibold">
                                                        {selectedEmployee?.name}
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                <div className="p-2 bg-light rounded">
                                                    <small className="text-muted">Date</small>
                                                    <div className="fw-semibold">{formatDate(absentData.date)}</div>
                                                </div>
                                            </Col>
                                        </Row>
                                        <FormGroup className="mt-2">
                                            <Label className="form-label-sm">Reason (Optional)</Label>
                                            <Input
                                                type="textarea"
                                                value={absentData.reason}
                                                onChange={(e) => setAbsentData(prev => ({ ...prev, reason: e.target.value }))}
                                                bsSize="sm"
                                                rows={2}
                                                placeholder="Enter reason for absence (optional)"
                                            />
                                        </FormGroup>
                                    </ModalBody>
                                    <ModalFooter>
                                        <Button color="secondary" onClick={() => setAbsentModal(false)}>
                                            Cancel
                                        </Button>
                                        <Button color="danger" onClick={handleMarkAbsentConfirm} disabled={markingAbsent}>
                                            {markingAbsent ? (
                                                <>
                                                    <Spinner size="sm" className="me-2" />
                                                    Processing...
                                                </>
                                            ) : (
                                                <>
                                                    <UserX size={14} className="me-1" />
                                                    Mark Absent
                                                </>
                                            )}
                                        </Button>
                                    </ModalFooter>
                                </Modal>

                                {/* ────────────────────────────────────────────── */}
                                {/* LOSS OF PAY — create / edit modal             */}
                                {/* ────────────────────────────────────────────── */}
                                <Modal
                                    isOpen={lopModal}
                                    toggle={() => !lopSubmitting && setLopModal(false)}
                                    size="md"
                                >
                                    <ModalHeader toggle={() => !lopSubmitting && setLopModal(false)}>
                                        <div className="d-flex align-items-center">
                                            <AlertCircle size={18} className="me-2" style={{ color: '#8b0000' }} />
                                            <div>
                                                <div className="fw-semibold">
                                                    {lopExistingId ? 'Edit Loss of Pay' : 'Mark as Loss of Pay'}
                                                </div>
                                                <small className="text-muted">
                                                    {selectedEmployee?.name} • {formatDate(selectedDate)}
                                                </small>
                                            </div>
                                        </div>
                                    </ModalHeader>

                                    <ModalBody>
                                        <div
                                            className="mb-3 p-3 rounded"
                                            style={{
                                                background: '#fff5f5',
                                                border: '2px solid #8b0000',
                                            }}
                                        >
                                            <div className="d-flex align-items-start gap-2">
                                                <span style={{ fontSize: '20px' }}>⚠️</span>
                                                <div className="small">
                                                    <div className="fw-bold mb-1" style={{ color: '#8b0000' }}>
                                                        One full day will be deducted from this month's salary.
                                                    </div>
                                                    <div className="text-muted">
                                                        This is a <strong>punishment entry</strong>. It applies even if
                                                        the employee was present, or it was a Sunday / holiday.
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mb-2">
                                            <label htmlFor="lopNotes" className="form-label fw-semibold small">
                                                Reason / Notes <span className="text-danger">*</span>
                                            </label>
                                            <textarea
                                                id="lopNotes"
                                                className="form-control"
                                                rows={3}
                                                placeholder="e.g. Punishment for misconduct on Sunday"
                                                value={lopNotes}
                                                onChange={(e) => setLopNotes(e.target.value)}
                                                disabled={lopSubmitting}
                                                autoFocus
                                                maxLength={500}
                                            />
                                            <small className="text-muted">
                                                This note is attached to the payroll deduction record.
                                                ({lopNotes.length}/500)
                                            </small>
                                        </div>

                                        {lopError && (
                                            <div className="alert alert-danger py-2 mb-0 small">{lopError}</div>
                                        )}
                                    </ModalBody>

                                    <ModalFooter>
                                        <Button
                                            color="secondary"
                                            onClick={() => setLopModal(false)}
                                            disabled={lopSubmitting}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={handleSubmitLop}
                                            disabled={lopSubmitting || !lopNotes.trim()}
                                            style={{
                                                backgroundColor: '#8b0000',
                                                borderColor: '#8b0000',
                                                color: '#fff',
                                                fontWeight: 700,
                                            }}
                                        >
                                            {lopSubmitting ? (
                                                <>
                                                    <span
                                                        className="spinner-border spinner-border-sm me-2"
                                                        role="status"
                                                        aria-hidden="true"
                                                    />
                                                    Saving…
                                                </>
                                            ) : (
                                                <>🚫 {lopExistingId ? 'Update' : 'Confirm Loss of Pay'}</>
                                            )}
                                        </Button>
                                    </ModalFooter>
                                </Modal>

                                {/* ────────────────────────────────────────────── */}
                                {/* LOSS OF PAY — detail modal (view existing)    */}
                                {/* ────────────────────────────────────────────── */}
                                <Modal
                                    isOpen={lopDetailModal}
                                    toggle={() => {
                                        setLopDetailModal(false);
                                        setLopDetail(null);
                                    }}
                                    size="md"
                                >
                                    <ModalHeader
                                        toggle={() => {
                                            setLopDetailModal(false);
                                            setLopDetail(null);
                                        }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <AlertCircle size={18} className="me-2" style={{ color: '#8b0000' }} />
                                            Loss of Pay
                                        </div>
                                    </ModalHeader>

                                    <ModalBody>
                                        {lopDetail && (
                                            <>
                                                <Row className="g-2 mb-3">
                                                    <Col md={6}>
                                                        <div className="p-2 bg-light rounded">
                                                            <small className="text-muted">Employee</small>
                                                            <div className="fw-semibold">{selectedEmployee?.name}</div>
                                                            <small className="text-muted">
                                                                {selectedEmployee?.employee_code}
                                                            </small>
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="p-2 bg-light rounded">
                                                            <small className="text-muted">Date</small>
                                                            <div className="fw-semibold">
                                                                {formatDate(selectedDate)}
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>

                                                <Row className="g-2">
                                                    <Col md={12}>
                                                        <div className="p-2 bg-light rounded">
                                                            <small className="text-muted">Reason</small>
                                                            <div style={{ whiteSpace: 'pre-wrap' }}>{lopDetail.notes}</div>
                                                        </div>
                                                    </Col>
                                                </Row>

                                                <div
                                                    className="mt-3 p-3 rounded"
                                                    style={{
                                                        background: '#fff5f5',
                                                        border: '2px solid #8b0000',
                                                    }}
                                                >
                                                    <small className="text-muted d-block mb-1">Salary Impact</small>
                                                    <div className="fw-bold" style={{ color: '#8b0000' }}>
                                                        One full day will be deducted from this month's salary.
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </ModalBody>

                                    <ModalFooter>
                                        <Button
                                            color="secondary"
                                            onClick={() => {
                                                setLopDetailModal(false);
                                                setLopDetail(null);
                                            }}
                                        >
                                            Close
                                        </Button>
                                        <Button
                                            color="warning"
                                            onClick={() => {
                                                if (lopDetail) {
                                                    setSelectedDate(lopDetail.date);
                                                    handleOpenLop(lopDetail.id, lopDetail.notes);
                                                }
                                            }}
                                        >
                                            <Edit2 size={14} className="me-1" /> Edit
                                        </Button>
                                        <Button
                                            onClick={() => lopDetail && handleDeactivateLop(lopDetail.id)}
                                            style={{
                                                backgroundColor: '#8b0000',
                                                borderColor: '#8b0000',
                                                color: '#fff',
                                                fontWeight: 700,
                                            }}
                                        >
                                            <X size={14} className="me-1" /> Remove
                                        </Button>
                                    </ModalFooter>
                                </Modal>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default Attendance;